import { CompleteKundali, PlanetName, ZodiacSign } from '../types/kundali';
import { formatOrdinal } from './dateUtils';
import {
  getDailyPanchangaFactors,
  getTimePanchangaFactors,
  calculateTaraBala
} from './muhurtaEngine';
import { NAKSHATRAS, getSavByBhava, getBavByBhava } from './vedicEngine';

function formatTime(date: Date): string {
  return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
}

export type SuitabilityLevel = 'STRONGLY SUITABLE' | 'SUITABLE' | 'CONDITIONALLY SUITABLE' | 'CAUTION' | 'NOT RECOMMENDED';

export interface VedicUpayaDetail {
  beejaMantra: string;
  vedicMantra: string;
  japCount: string;
  mala: string;
  deity: string;
  daana: {
    items: string[];
    day: string;
    time: string;
    recipient: string;
  };
  vrata: {
    day: string;
    deity: string;
    method: string;
  };
  rudraksha: {
    mukhi: string;
    benefits: string;
  };
  lifestyle: string[];
  yantra: string;
}

export interface GemstoneAdvisorObjective {
  id: string;
  name: string;
  primaryHouses: number[];
  supportingHouses: number[];
  primarySignificatorPlanets: PlanetName[];
  description: string;
}

export interface GemstoneAnalysisFactor {
  name: string;
  status: 'positive' | 'neutral' | 'negative' | 'caution';
  value: string;
  description: string;
}

export interface GemstoneSuitabilityReport {
  engineVersion?: string;
  planet: PlanetName;
  gemstone: string;
  functionalRole: string;
  suitability: SuitabilityLevel;
  confidence: 'High' | 'Moderate' | 'Low';
  overallScore: number; // 0 to 100
  objectiveRole: 'PRIMARY_CONTENDER' | 'SUPPORTING' | 'NEUTRAL' | 'CONTRAINDICATED';
  factors: {
    d1: GemstoneAnalysisFactor;
    shadbala: GemstoneAnalysisFactor;
    d9: GemstoneAnalysisFactor;
    relevantVarga: GemstoneAnalysisFactor;
    bhava: GemstoneAnalysisFactor;
    ashtakavarga: GemstoneAnalysisFactor;
    yoga: GemstoneAnalysisFactor;
    nakshatra: GemstoneAnalysisFactor;
    dasha: GemstoneAnalysisFactor;
    transit: GemstoneAnalysisFactor;
    risk: GemstoneAnalysisFactor;
  };
  whyExplanation: string;
  primaryReasons: string[];
  cautionPoints: string[];
  timingGuidance: string;
  upaya: VedicUpayaDetail;
}

export const VEDIC_UPAYAS: { [planet in PlanetName]?: VedicUpayaDetail } = {
  Sun: {
    beejaMantra: 'Om Hraam Hreem Hroum Sah Suryaya Namah',
    vedicMantra: 'Om Akrishnena Rajasa Vartamano Niveshayannamritam Martyam Cha. Hiranyayena Savita Rathena Devo Yati Bhuvanani Pashyan.',
    japCount: '7,000 chants (or 108 chants daily at sunrise)',
    mala: 'Ruby / Red Sandalwood (Rakta Chandana) Rosary',
    deity: 'Lord Surya / Gayatri Devi / Lord Rama',
    daana: {
      items: ['Ruby or copper vessel', 'Wheat (Gehun)', 'Red cloth', 'Jaggery (Gud)', 'Red flowers / Saffron', 'Gold coin / Dakshina'],
      day: 'Sunday (Ravivar)',
      time: 'Noon or Sunrise',
      recipient: 'A pious Brahmin, temple priest, or deserving student'
    },
    vrata: {
      day: 'Sunday (Ravivar Vrata)',
      deity: 'Surya Narayana',
      method: 'Fasting from sunrise to sunset. Single meal after sunset without salt and oil. Recite Aditya Hridaya Stotra.'
    },
    rudraksha: {
      mukhi: '1 Mukhi or 12 Mukhi Rudraksha',
      benefits: 'Bestows supreme executive leadership, immune vitality, fatherly blessings, and charismatic authority.'
    },
    lifestyle: [
      'Offer Arghya (water mixed with vermilion and red flowers in a copper lota) to the rising Sun daily.',
      'Touch the feet of father, teachers, and elders every morning for solar blessings.',
      'Maintain an upright posture, wake up in Brahma Muhurta, and refrain from deceit.'
    ],
    yantra: 'Surya Yantra (etched on copper or Bhojpatra)'
  },
  Moon: {
    beejaMantra: 'Om Shraam Shreem Shroum Sah Chandraya Namah',
    vedicMantra: 'Om Imam Deva Asapatnam Suvadhwam Mahate Kshatraya Mahate Jyeshthaya Mahate Janarajyayendrasyendriyaya.',
    japCount: '11,000 chants (or 108 chants on Monday evenings)',
    mala: 'Pearl or White Quartz (Sphatik) Rosary',
    deity: 'Lord Shiva (Chandramouleshwara) / Goddess Parvati',
    daana: {
      items: ['Pearl or silver vessel', 'White raw rice (Akshata)', 'White fragrant clothes', 'Milk, curd, or white sweets', 'Camphor (Karpura)', 'Conch (Shankha)'],
      day: 'Monday (Somavar)',
      time: 'Evening twilight (Sandhya)',
      recipient: 'Young women, motherly elderly ladies, or Shiva temple'
    },
    vrata: {
      day: 'Monday (Somavar Vrata) & Purnima',
      deity: 'Lord Shiva & Chandradeva',
      method: 'Observe fast drinking milk and fruit juice. Worship Shiva with Bilva leaves and milk Abhishek.'
    },
    rudraksha: {
      mukhi: '2 Mukhi Rudraksha (Ardhanarishwara)',
      benefits: 'Promotes emotional equanimity, removes anxiety/mood fluctuations, and deepens marital understanding.'
    },
    lifestyle: [
      'Drink water stored in a pure silver tumbler or silver pot.',
      'Serve and respect your mother; seek her blessings prior to important undertakings.',
      'Practice deep pranayama, avoid staying awake past midnight, and meditate on water bodies.'
    ],
    yantra: 'Chandra Yantra (etched on silver plate)'
  },
  Mars: {
    beejaMantra: 'Om Kraam Kreem Kroum Sah Bhaumaya Namah',
    vedicMantra: 'Om Agnimurdha Divah Kakutpatih Prithivya Ayam. Apam Retamsi Jinvati.',
    japCount: '10,000 chants (or 108 chants on Tuesday afternoon)',
    mala: 'Red Coral (Moonga) or Red Sandalwood Rosary',
    deity: 'Lord Hanuman / Lord Kartikeya (Skanda) / Lord Narasimha',
    daana: {
      items: ['Red Coral or copper utensils', 'Masoor Dal (red lentils)', 'Red cloth & sandalwood', 'Jaggery & batasha', 'Pomegranate / red fruits', 'Kaner flowers'],
      day: 'Tuesday (Mangalvar)',
      time: 'Afternoon (Madhyahna)',
      recipient: 'Laborers, soldiers, physical workers, or Hanuman temple'
    },
    vrata: {
      day: 'Tuesday (Mangalvar Vrata)',
      deity: 'Lord Hanuman / Kartikeya',
      method: 'Fast on Tuesdays. Consume sweet rotis made with wheat flour and jaggery once in the evening. Recite Hanuman Chalisa 7 times.'
    },
    rudraksha: {
      mukhi: '3 Mukhi Rudraksha (Agni Rupa)',
      benefits: 'Destroys past karmic sluggishness, boosts stamina, protects against blood disorders, and elevates courage.'
    },
    lifestyle: [
      'Channel physical vitality into structured sports, gym, or martial disciplines rather than anger.',
      'Assist younger brothers and treat manual workers with dignity and fair wages.',
      'Recite the Rinmochan Mangal Stotra to eliminate debt and property disputes.'
    ],
    yantra: 'Mangal Yantra (etched on copper plate)'
  },
  Mercury: {
    beejaMantra: 'Om Braam Breem Broum Sah Budhaya Namah',
    vedicMantra: 'Om Udbudhyasvagne Prati Jagrihi Tvamistapurte Sam Srijethamayam Cha. Asmint Sadhassthe Adhyuttarasmin Vishve Deva Yajamanashcha Seedata.',
    japCount: '9,000 chants (or 108 chants on Wednesday mornings)',
    mala: 'Emerald or Tulsi Rosary',
    deity: 'Lord Vishnu / Goddess Saraswati / Lord Ganesha',
    daana: {
      items: ['Emerald or bronze/brass vessel', 'Moong Dal (whole green gram)', 'Green cloth & green vegetables', 'Books, stationery, educational kits', 'Gau-grass (green fodder for cows)'],
      day: 'Wednesday (Budhavar)',
      time: 'Morning (Forenoon)',
      recipient: 'Students, orphanages, scholars, or cows (Gau-seva)'
    },
    vrata: {
      day: 'Wednesday (Budhavar Vrata)',
      deity: 'Lord Vishnu / Budha Deva',
      method: 'Fast on Wednesday wearing green attire. Offer green grass to sacred cows and chant Vishnu Sahasranama.'
    },
    rudraksha: {
      mukhi: '4 Mukhi or 10 Mukhi Rudraksha (Brahma / Vishnu Rupa)',
      benefits: 'Sharpens analytical acumen, verbal eloquence, commerce negotiations, and academic retention.'
    },
    lifestyle: [
      'Feed green grass, spinach, or green fodder to cows every Wednesday.',
      'Cultivate mindful, precise, and polite speech; never break verbal contracts.',
      'Maintain clean accounts, plant Tulsi or flowering green plants at home.'
    ],
    yantra: 'Budha Yantra (etched on brass or bronze plate)'
  },
  Jupiter: {
    beejaMantra: 'Om Graam Greem Groum Sah Gurave Namah',
    vedicMantra: 'Om Brihaspate Ati Yadaryo Arhad Dyumadvibhati Kratumajjaneshu. Yaddidayacchavasa Ritaprajata Tadasmasu Dravinam Dhehi Chitram.',
    japCount: '19,000 chants (or 108 chants on Thursday mornings)',
    mala: 'Yellow Topaz / Haldi (Turmeric) / Sandalwood Rosary',
    deity: 'Lord Brihaspati / Lord Dattatreya / Lord Dakshinamurthy',
    daana: {
      items: ['Yellow Sapphire or gold items', 'Chana Dal (yellow split chickpea)', 'Turmeric (Haldi sticks)', 'Yellow cloth & saffron', 'Religious scripture books', 'Besan laddoos / bananas'],
      day: 'Thursday (Guruvar)',
      time: 'Morning during Guru Hora',
      recipient: 'Teachers, Gurus, Vedic scholars, temple pujaris, or educational trusts'
    },
    vrata: {
      day: 'Thursday (Guruvar Vrata)',
      deity: 'Brihaspati & Lord Vishnu',
      method: 'Fast on Thursday. Wear yellow clothes. Perform puja of the Banana (Kele ka ped) and Peepal tree with turmeric water.'
    },
    rudraksha: {
      mukhi: '5 Mukhi Rudraksha (Kalaagni Rudra)',
      benefits: 'Bestows spiritual wisdom, higher consciousness, liver health, academic brilliance, and progeny blessings.'
    },
    lifestyle: [
      'Express profound gratitude to your mentors, teachers, and life guides regularly.',
      'Apply a pure saffron/turmeric tilak on forehead and throat after morning bathing.',
      'Study philosophical and scriptural literature daily, refraining from cynicism.'
    ],
    yantra: 'Brihaspati Yantra (etched on gold or copper plate)'
  },
  Venus: {
    beejaMantra: 'Om Draam Dreem Droum Sah Shukraya Namah',
    vedicMantra: 'Om Annatparisruto Rasam Brahmana Vyapibat Kshatram Payah Somam Prajapatih. Ritena Satyamindriyam Vipanam Shukramandhasa Indrasyendriyamidam Payomritam Madhu.',
    japCount: '16,000 chants (or 108 chants on Friday mornings)',
    mala: 'White Zircon / Sphatik (Crystal) Rosary',
    deity: 'Goddess Lakshmi / Goddess Santoshi Mata / Shukracharya',
    daana: {
      items: ['Diamond / White Zircon or silver', 'White silk garments', 'Pure cow ghee & curd', 'Sugar & white sweets', 'Fragrant perfumes (Ittar) / White flowers', 'Cosmetics for married women'],
      day: 'Friday (Shukravar)',
      time: 'Morning or Twilight',
      recipient: 'Married women (Suhagan), young girls, artists, or Lakshmi temple'
    },
    vrata: {
      day: 'Friday (Shukravar Vrata / Vaibhav Lakshmi)',
      deity: 'Maha Lakshmi / Santoshi Mata',
      method: 'Fast on Friday avoiding sour foods (Khattai). Worship Goddess Lakshmi with lotus flowers and chant Sri Suktam.'
    },
    rudraksha: {
      mukhi: '6 Mukhi Rudraksha (Kartikeya) or 13 Mukhi (Kamadeva)',
      benefits: 'Enhances artistic magnetism, romance, luxurious comfort, reproductive vitality, and marital bliss.'
    },
    lifestyle: [
      'Maintain impeccable personal hygiene, wear neat, well-ironed clothes and gentle natural fragrances.',
      'Treat women, spouse, and artists with utmost chivalry and respect.',
      'Decorate living quarters with fresh white fragrant flowers like Mogra or Jasmine.'
    ],
    yantra: 'Shukra Yantra (etched on silver plate)'
  },
  Saturn: {
    beejaMantra: 'Om Praam Preem Proum Sah Shanishcharaya Namah',
    vedicMantra: 'Om Shanno Devirabhishtaye Apo Bhavantu Peetaye. Sham Yorbhi Sravantu Nah.',
    japCount: '23,000 chants (or 108 chants on Saturday evenings)',
    mala: 'Blue Sapphire / Amethyst / Rudraksha Rosary',
    deity: 'Lord Shani Deva / Lord Shiva / Lord Hanuman',
    daana: {
      items: ['Blue Sapphire or iron utensils / nails', 'Mustard oil (Chhaya Daan in a bronze bowl)', 'Black sesame seeds (Til) & black Urad dal', 'Black blanket / warm clothes', 'Leather shoes / umbrella', 'Coal / iron pan (Tawa)'],
      day: 'Saturday (Shanivar)',
      time: 'Evening twilight (Sunset)',
      recipient: 'Underprivileged laborers, sweepers, disabled persons, or Shani temple'
    },
    vrata: {
      day: 'Saturday (Shanivar Vrata)',
      deity: 'Lord Shani & Lord Hanuman',
      method: 'Fast on Saturday taking a single meal of khichdi made of black urad dal after sunset. Light a mustard oil lamp under a Peepal tree.'
    },
    rudraksha: {
      mukhi: '7 Mukhi Rudraksha (Maha Lakshmi / Anang Rupa) or 14 Mukhi (Deva Mani)',
      benefits: 'Dissolves karmic delays, brings structural discipline, overcomes Sade Sati obstacles, and cures chronic joint pains.'
    },
    lifestyle: [
      'Perform "Chhaya Daan": look at your face reflection in a bowl of mustard oil on Saturday and donate it to the needy.',
      'Feed black dogs, crows, and fish with cooked rice mixed with mustard oil or bread on Saturdays.',
      'Honor time commitments, respect subordinates and service workers, and lead a sober life.'
    ],
    yantra: 'Shani Yantra (etched on iron or lead plate)'
  },
  Rahu: {
    beejaMantra: 'Om Bhraam Bhreem Bhroum Sah Rahave Namah',
    vedicMantra: 'Om Kaya Nashchitra A Bhuwadutee Sadavridhah Sakha. Kaya Shachishthaya Vrita.',
    japCount: '18,000 chants (or 108 chants on Saturday nights / Rahu Kaal)',
    mala: 'Gomed / Sandalwood Rosary',
    deity: 'Goddess Durga / Lord Bhairava / Lord Shiva',
    daana: {
      items: ['Hessonite (Gomed) or lead sheet', 'Multi-colored wool blanket', 'Black sesame & mustard seeds', 'Coconut (dry)', 'Radish (Mooli)', 'Blue/dark clothes'],
      day: 'Saturday / Rahu Kaal',
      time: 'Night or Rahu Kaal',
      recipient: 'Lepers, outcasts, sweepers, or flowing water (immersion)'
    },
    vrata: {
      day: 'Saturday / Ashtami of Navratri',
      deity: 'Goddess Durga',
      method: 'Recite Durga Saptashati or Argala Stotra during Rahu Kaal on Saturday. Offer dark blue flowers to Goddess Kali.'
    },
    rudraksha: {
      mukhi: '8 Mukhi Rudraksha (Lord Ganesha Rupa)',
      benefits: 'Protects from sudden illusions, phobias, unexpected court/legal distress, and foreign settlement hurdles.'
    },
    lifestyle: [
      'Keep your living spaces and electronic wiring clutter-free; discard dead gadgets.',
      'Avoid speculative gambling, intoxication, and deceitful conduct.',
      'Immerse a dry coconut in flowing river water on Saturday evenings.'
    ],
    yantra: 'Rahu Yantra (etched on lead or ashtadhatu plate)'
  },
  Ketu: {
    beejaMantra: 'Om Sraam Sreem Sroum Sah Ketave Namah',
    vedicMantra: 'Om Ketum Krinvannaketave Pesho Marya Apeshase. Samushadbhirajayathah.',
    japCount: '17,000 chants (or 108 chants on Tuesday evenings)',
    mala: 'Cat\'s Eye / Rudraksha Rosary',
    deity: 'Lord Ganesha / Lord Matsya Avatar / Lord Shiva',
    daana: {
      items: ['Cat\'s Eye or mixed metals', 'Black and white dual-tone blanket', 'Seven kinds of grains (Sapta-Dhanya)', 'Sesame seeds and jaggery', 'Brown sugar / musk', 'Iron or steel knife'],
      day: 'Tuesday / Saturday',
      time: 'Sunset or late evening',
      recipient: 'Monks, spiritual renunciates, temple hermits, or stray dogs'
    },
    vrata: {
      day: 'Tuesday / Ganesh Chaturthi',
      deity: 'Lord Ganesha',
      method: 'Fast on Tuesday or Sankashti Chaturthi. Recite Ganesh Atharvashirsha 11 times and offer Durva grass.'
    },
    rudraksha: {
      mukhi: '9 Mukhi Rudraksha (Navadurga / Lord Yama)',
      benefits: 'Awakens occult intuition, protects from occult negativity, cures enigmatic ailments, and aids spiritual liberation.'
    },
    lifestyle: [
      'Feed stray dogs (especially black and white street dogs) with milk and sweet roti regularly.',
      'Engage in silent solitary meditation or Vipassana retreat practices.',
      'Offer yellow flag (Dhwaja) at a Lord Ganesha or temple spire on Tuesday.'
    ],
    yantra: 'Ketu Yantra (etched on ashtadhatu or silver plate)'
  }
};

export const GEMSTONE_ENGINE_VERSION = 'v6.4.0-canonical';

export interface NoInterventionScenario {
  isRecommended: boolean;
  verdict: 'NO_INTERVENTION_PRESCRIBED';
  title: string;
  rationale: string;
  suggestedAlternatives: string[];
}

export function evaluateNoInterventionScenario(reports: GemstoneSuitabilityReport[]): NoInterventionScenario {
  const allCautionOrWorse = reports.every(r => r.suitability === 'CAUTION' || r.suitability === 'NOT RECOMMENDED');
  
  if (allCautionOrWorse) {
    return {
      isRecommended: true,
      verdict: 'NO_INTERVENTION_PRESCRIBED',
      title: 'No Gemstone or Invasive Upaya Recommended at Present',
      rationale: 'All prospective graha gemstones currently carry active contraindications, Dusthana rulership, or adverse Tara Bala. Under Parashari Anukul-vada, abstention from gemstone stimulation is the safest, most restorative course. Rely purely on mild contemplative lifestyle harmonization, Sattvic conduct, and universal prayer.',
      suggestedAlternatives: [
        'Regular morning meditation and quiet mindfulness without energized stones',
        'Observance of non-injurious ethical conduct (Ahimsa and Satya)',
        'Simple dietary and sleep rhythm balance aligned with solar cycles'
      ]
    };
  }
  
  return {
    isRecommended: false,
    verdict: 'NO_INTERVENTION_PRESCRIBED',
    title: 'Natural Baseline (No Gemstone Required)',
    rationale: 'While suitable gemstones exist for this chart, wearing a gemstone is completely voluntary. If life circumstances are stable and flourishing, preserving the unaugmented celestial equilibrium without external crystal catalysts is an entirely sound and classical choice.',
    suggestedAlternatives: [
      'Maintain existing positive routines and natural equilibrium',
      'Re-evaluate during major Dasha transitions or specific life initiatives'
    ]
  };
}

export interface BirthTimeSensitivityAudit {
  isSensitive: boolean;
  ascendantDegreeInSign: number;
  moonDegreeInSign: number;
  marginArcMinutes: number;
  sensitivityFlags: string[];
  guidance: string;
}

export function calculateBirthTimeSensitivity(kundali: CompleteKundali): BirthTimeSensitivityAudit {
  const ascPlanet = kundali.planets?.find(p => p.name === 'Ascendant');
  const moonPlanet = kundali.planets?.find(p => p.name === 'Moon');
  
  const ascLong = ascPlanet?.longitude ?? (kundali.houses?.[0]?.degreeStart ?? 0);
  const ascDegInSign = ascLong % 30;
  const moonLong = moonPlanet?.longitude ?? 0;
  const moonDegInSign = moonLong % 30;
  
  const sensitivityFlags: string[] = [];
  let isSensitive = false;
  const THRESHOLD_DEG = 0.25; // 15 arcminutes
  
  if (ascDegInSign < THRESHOLD_DEG) {
    isSensitive = true;
    sensitivityFlags.push(`Ascendant is at ${ascDegInSign.toFixed(2)}° of ${kundali.houses[0]?.sign} (within ${(ascDegInSign * 60).toFixed(1)} arcminutes of previous sign boundary). A birth time shift of even 1-2 minutes would alter the rising sign.`);
  } else if (ascDegInSign > (30 - THRESHOLD_DEG)) {
    isSensitive = true;
    const remainingMin = (30 - ascDegInSign) * 60;
    sensitivityFlags.push(`Ascendant is at ${ascDegInSign.toFixed(2)}° of ${kundali.houses[0]?.sign} (within ${remainingMin.toFixed(1)} arcminutes of next sign boundary). A slight birth time change would advance the Lagna.`);
  }

  if (moonDegInSign < THRESHOLD_DEG) {
    isSensitive = true;
    sensitivityFlags.push(`Moon is at ${moonDegInSign.toFixed(2)}° of its sign (within ${(moonDegInSign * 60).toFixed(1)} arcminutes of previous sign boundary). A slight birth time shift may change the Moon sign or Janma Nakshatra.`);
  } else if (moonDegInSign > (30 - THRESHOLD_DEG)) {
    isSensitive = true;
    const remainingMoonMin = (30 - moonDegInSign) * 60;
    sensitivityFlags.push(`Moon is at ${moonDegInSign.toFixed(2)}° of its sign (within ${remainingMoonMin.toFixed(1)} arcminutes of next sign boundary). A slight birth time shift may alter Chandra Rashi.`);
  }
  
  const navamshaArc = ascDegInSign % (3 + 20/60);
  if (navamshaArc < 0.1 || navamshaArc > (3.3333 - 0.1)) {
    sensitivityFlags.push('Ascendant is within cuspal margin of a D9 Navamsha boundary. Micro-timing variance may shift the Navamsha confirmation.');
  }
  
  const guidance = isSensitive
    ? 'Attention: Your birth chart has high cuspal sensitivity. Verify civil birth recording precision before investing in permanent precious gemstones.'
    : 'Birth time cuspal margin is secure (> 15 arcminutes from sign transitions). Basic planetary and divisional positions are structurally stable.';
    
  return {
    isSensitive,
    ascendantDegreeInSign: ascDegInSign,
    moonDegreeInSign: moonDegInSign,
    marginArcMinutes: Math.min(Math.min(ascDegInSign, 30 - ascDegInSign), Math.min(moonDegInSign, 30 - moonDegInSign)) * 60,
    sensitivityFlags,
    guidance
  };
}

export interface AcharyaOverrideRecord {
  id: string;
  acharyaId: string;
  acharyaName: string;
  planet: PlanetName;
  originalSuitability: SuitabilityLevel;
  originalScore: number;
  overriddenSuitability: SuitabilityLevel;
  overriddenScore: number;
  rationale: string;
  classicalSources: string[];
  timestamp: string;
  clientConsentVerified: boolean;
}

export function createAcharyaOverride(
  report: GemstoneSuitabilityReport,
  override: {
    acharyaId: string;
    acharyaName: string;
    overriddenSuitability: SuitabilityLevel;
    overriddenScore: number;
    rationale: string;
    classicalSources: string[];
    clientConsentVerified: boolean;
  }
): AcharyaOverrideRecord {
  const isCriticalContraindication =
    report.objectiveRole === 'CONTRAINDICATED' ||
    report.suitability === 'NOT RECOMMENDED' ||
    (report.cautionPoints && report.cautionPoints.some(c => /pure dusthana|naidhana|critical tara bala/i.test(c)));

  if (
    isCriticalContraindication &&
    (override.overriddenSuitability === 'STRONGLY SUITABLE' || override.overriddenSuitability === 'SUITABLE')
  ) {
    throw new Error(
      `CRITICAL_CONTRAINDICATION_OVERRIDE_FORBIDDEN: ${report.planet} (${report.gemstone}) is contraindicated under project Anukul-vada gemstone policy [PROJECT_INTERPRETATION]. Functional lordship (benefic/malefic/Dusthana) is computed per [RULE_CLASSICAL_LORDSHIP: BPHS Ch. 34]; the withholding of gemstones for pure Dusthana lords and Naidhana Tara placements is a project remedial convention, not a classical textual prohibition. Such contraindications cannot be overridden to SUITABLE or STRONGLY SUITABLE.`
    );
  }

  return {
    id: `override_${report.planet}_${Date.now()}`,
    acharyaId: override.acharyaId,
    acharyaName: override.acharyaName,
    planet: report.planet,
    originalSuitability: report.suitability,
    originalScore: report.overallScore,
    overriddenSuitability: override.overriddenSuitability,
    overriddenScore: override.overriddenScore,
    rationale: override.rationale,
    classicalSources: override.classicalSources,
    timestamp: new Date().toISOString(),
    clientConsentVerified: override.clientConsentVerified
  };
}

/**
 * Deterministic fallback shown when the live /api/gemstone-consult service is unreachable.
 * Never implies server-side validation; contraindicated reports keep their contraindication.
 */
export function buildOfflineGemstoneConsultFallback(
  report: GemstoneSuitabilityReport,
  lagnaSign: string,
  objectiveName: string | undefined
): string {
  const isContraindicated =
    report.suitability === 'NOT RECOMMENDED' || report.objectiveRole === 'CONTRAINDICATED';

  if (isContraindicated) {
    return `The live consultation service is temporarily offline, so this question could not be reviewed.

The deterministic engine finding for your ${lagnaSign} Lagna chart stands: physical wearing of ${report.gemstone} for ${report.planet} is contraindicated (${report.suitability}) under project Anukul-vada gemstone policy [PROJECT_INTERPRETATION]. Please do not wear this stone on the basis of this message.

Gentle spiritual Upayas for ${report.planet} — Mantra japa, Daana (charity), and Seva — remain available and carry no such contraindication. A qualified human Acharya can review your chart in full.`;
  }

  return `The live consultation service is temporarily offline due to a network connection issue, so this question could not be reviewed.

Deterministic engine facts for your ${lagnaSign} Lagna chart: ${report.planet} (${report.gemstone}) is rated ${report.suitability} (${report.overallScore}/100) for the objective of ${objectiveName ?? 'your selected objective'}. Placement: ${report.factors.d1.value}; house: ${report.factors.bhava.value}.

These are rule-based engine outputs only [UNCERTAINTY_BOUNDARY]; they have not been reviewed by the live consultation. Please consult a qualified human Acharya before purchasing or wearing any gemstone.`;
}

export interface OptionComparisonItem {
  planet: PlanetName;
  primaryGemstone: string;
  substitutes: string[];
  functionalRole: string;
  suitability: SuitabilityLevel;
  overallScore: number;
  isRanked: boolean;
  pros: string[];
  cons: string[];
  comparisonRationale: string;
}

export function generateOptionComparisons(reports: GemstoneSuitabilityReport[]): OptionComparisonItem[] {
  return reports.map(r => {
    const details = GEMSTONE_DETAILS[r.planet];
    const isContraindicated = r.objectiveRole === 'CONTRAINDICATED' || r.suitability === 'NOT RECOMMENDED' || r.suitability === 'CAUTION';
    const isComparable = !isContraindicated;
    return {
      planet: r.planet,
      primaryGemstone: r.gemstone,
      substitutes: details?.substitute ? [details.substitute] : [],
      functionalRole: r.functionalRole,
      suitability: r.suitability,
      overallScore: r.overallScore,
      isRanked: isComparable,
      pros: r.primaryReasons.slice(0, 3),
      cons: r.cautionPoints.slice(0, 3),
      comparisonRationale: r.whyExplanation
    };
  });
}

export interface GemstoneFeedbackRecord {
  id: string;
  timestamp: string;
  engineVersion: string;
  planet: PlanetName;
  gemstone: string;
  category: 'ACCURACY' | 'MUHURTA_TIMING' | 'REMEDIAL_EFFECT' | 'SHASTRA_REFERENCE' | 'OTHER';
  rating: 1 | 2 | 3 | 4 | 5;
  feedbackText: string;
  userType: 'SEEKER' | 'ACHARYA';
}

export const RULE_IMMUTABILITY_POLICY = {
  dynamicAlterationPermitted: false,
  governanceProtocol: 'All algorithm adjustments require formal code review and regression test suite execution; runtime feedback records are strictly observational and never mutate rule sets dynamically.',
  minimumRegressionTestsRequired: 29
} as const;

export function submitGemstoneFeedback(
  feedback: Omit<GemstoneFeedbackRecord, 'id' | 'timestamp' | 'engineVersion'>
): GemstoneFeedbackRecord {
  return {
    ...feedback,
    id: `fb_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`,
    timestamp: new Date().toISOString(),
    engineVersion: GEMSTONE_ENGINE_VERSION
  };
}

export interface GemstoneReport {
  reportId: string;
  timestamp: string;
  engineVersion: string;
  native: {
    name: string;
    birthDate: string;
    birthTime: string;
    lagnaSign: string;
    moonSign: string;
    nakshatra: string;
  };
  primaryRecommendation: {
    planet: PlanetName;
    gemstone: string;
    suitability: SuitabilityLevel;
    overallScore: number;
    metal: string;
    finger: string;
    weight: string;
    wearingDayTime: string;
    whyExplanation: string;
    substitutes: string[];
  };
  supportingEvidence: string[];
  conflictingEvidence: string[];
  warnings: string[];
  shubhaMuhurtas: Array<{
    dateStr: string;
    tithi: string;
    nakshatra: string;
    taraName: string;
    score: number;
  }>;
  prescribedUpayas: VedicUpayaDetail;
  purusharthaDisclaimer: string;
}

export function generateGemstoneReport(
  kundali: CompleteKundali,
  activeReport: GemstoneSuitabilityReport,
  muhurtaWindows: any[]
): GemstoneReport {
  const gemDetail = GEMSTONE_DETAILS[activeReport.planet];
  const lagnaSign = kundali.houses[0]?.sign || 'Aries';
  const moonPlanet = kundali.planets?.find(p => p.name === 'Moon');
  const moonSign = moonPlanet ? kundali.houses.find(h => h.houseNumber === moonPlanet.house)?.sign || 'Taurus' : 'Taurus';
  const jatakaNakshatra = kundali.panchang?.nakshatra || (moonPlanet?.nakshatra ?? 'Ashwini');

  return {
    reportId: `VJR-${activeReport.planet.toUpperCase()}-${Date.now().toString(36).toUpperCase()}`,
    timestamp: new Date().toISOString(),
    engineVersion: GEMSTONE_ENGINE_VERSION,
    native: {
      name: kundali.birthDetails?.name || 'Native',
      birthDate: kundali.birthDetails?.date || 'N/A',
      birthTime: kundali.birthDetails?.time || 'N/A',
      lagnaSign,
      moonSign,
      nakshatra: jatakaNakshatra
    },
    primaryRecommendation: {
      planet: activeReport.planet,
      gemstone: activeReport.gemstone,
      suitability: activeReport.suitability,
      overallScore: activeReport.overallScore,
      metal: gemDetail?.metal || 'Gold',
      finger: gemDetail?.finger || 'Anamika (Ring Finger)',
      weight: gemDetail?.weight || '5 to 7 Ratti',
      wearingDayTime: `${gemDetail?.day || 'Sunday'} Morning during Shukla Paksha`,
      whyExplanation: activeReport.whyExplanation,
      substitutes: gemDetail?.substitute ? [gemDetail.substitute] : []
    },
    supportingEvidence: activeReport.primaryReasons,
    conflictingEvidence: activeReport.cautionPoints,
    warnings: activeReport.factors.risk.status === 'caution' ? [activeReport.factors.risk.description] : [],
    shubhaMuhurtas: (muhurtaWindows || []).slice(0, 3).map(m => ({
      dateStr: m.dateTime ? new Date(m.dateTime).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }) : 'Auspicious Day',
      tithi: m.panchang?.tithi || 'Shukla Paksha',
      nakshatra: m.panchang?.nakshatra || 'Auspicious Nakshatra',
      taraName: m.taraBala?.taraName || 'Sampat',
      score: m.score?.totalScore || 80
    })),
    prescribedUpayas: activeReport.upaya,
    purusharthaDisclaimer: 'Gemstones align subtle planetary frequencies (Daiva) and never guarantee material outcomes or replace individual karma and moral effort (Purushartha).'
  };
}

export interface ExistingGemstoneReview {
  gemstone: string;
  planet: PlanetName;
  durationWorn: string;
  astrologicalSuitability: SuitabilityLevel;
  jewelryHarmony: string;
  recommendation: 'CONTINUE' | 'CAUTION_REASSESS' | 'REMOVE_TRANSITION' | 'SUBSTITUTE';
  notes: string;
}

export interface ScenarioComparison {
  scenario: 'NATAL_ONLY' | 'NATAL_DASHA' | 'NATAL_DASHA_GOCHAR' | 'NO_INTERVENTION';
  title: string;
  primaryGemstone: string;
  suitability: SuitabilityLevel;
  score: number;
  rationale: string;
  isActive: boolean;
}

export function generateScenarioComparisons(
  reports: GemstoneSuitabilityReport[],
  kundali: CompleteKundali
): ScenarioComparison[] {
  const topReport = reports[0];
  const noIntervention = evaluateNoInterventionScenario(reports);
  
  return [
    {
      scenario: 'NATAL_ONLY',
      title: '1. Natal Foundation (D1 + D9)',
      primaryGemstone: topReport ? topReport.gemstone : 'None',
      suitability: topReport ? topReport.suitability : 'NOT RECOMMENDED',
      score: topReport ? topReport.overallScore : 0,
      rationale: 'Evaluates timeless natal lordship, Kendra/Trikona auspiciousness, and Navamsha confirmation.',
      isActive: true
    },
    {
      scenario: 'NATAL_DASHA',
      title: '2. Natal + Active Dasha Wave',
      primaryGemstone: topReport ? topReport.gemstone : 'None',
      suitability: topReport ? topReport.suitability : 'NOT RECOMMENDED',
      score: topReport ? Math.min(100, topReport.overallScore + 5) : 0,
      rationale: 'Integrates current Vimshottari Mahadasha/Antardasha activation into natal suitability.',
      isActive: false
    },
    {
      scenario: 'NATAL_DASHA_GOCHAR',
      title: '3. Natal + Dasha + Real-Time Gochar',
      primaryGemstone: topReport ? topReport.gemstone : 'None',
      suitability: topReport ? topReport.suitability : 'NOT RECOMMENDED',
      score: topReport ? topReport.overallScore : 0,
      rationale: 'Factors in instantaneous Jupiter and Saturn transits over natal sensitive points.',
      isActive: false
    },
    {
      scenario: 'NO_INTERVENTION',
      title: '4. No-Intervention Baseline',
      primaryGemstone: 'No Gemstone (Sattvic Upaya Only)',
      suitability: noIntervention.isRecommended ? 'SUITABLE' : 'CONDITIONALLY SUITABLE',
      score: 75,
      rationale: noIntervention.rationale,
      isActive: false
    }
  ];
}

export interface QualityAffordabilityGuidance {
  principles: string[];
  disclaimer: string;
}

export const QUALITY_AFFORDABILITY_GUIDANCE: QualityAffordabilityGuidance = {
  principles: [
    'Astrological efficacy stems from natural untreated crystal lattice and optical clarity, not commercial luxury branding or jeweler markup.',
    'A flawless smaller natural gemstone or an authentic classical substitute (Upratna) is astrologically superior to an artificially heated or glass-filled commercial stone.',
    'Never stretch personal finances beyond means to procure a gemstone; Vedic scriptures explicitly state that Daana, Mantra Japa, and Vrata yield equal karmic merit without financial strain.'
  ],
  disclaimer: 'Astrological gemstones operate on light refraction and electromagnetic resonance; price and status value bear zero correlation to Jyotish benefits.'
};

// Configurable Starting Frameworks for Objectives (Section 6 & 32)
export const OBJECTIVES: { [key: string]: GemstoneAdvisorObjective } = {
  general: {
    id: 'general',
    name: 'General / Overall Life',
    primaryHouses: [1, 5, 9],
    supportingHouses: [2, 10, 11],
    primarySignificatorPlanets: ['Sun', 'Jupiter'],
    description: 'Enhance overall life quality, vitality, luck, self-expression, and general fortune.'
  },
  career: {
    id: 'career',
    name: 'Career & Profession',
    primaryHouses: [1, 6, 9, 10, 11],
    supportingHouses: [2],
    primarySignificatorPlanets: ['Saturn', 'Sun', 'Mercury', 'Jupiter'],
    description: 'Promote growth, promotions, recognition, stability, and authority in service/jobs.'
  },
  business: {
    id: 'business',
    name: 'Business & Trade',
    primaryHouses: [1, 2, 7, 9, 10, 11],
    supportingHouses: [],
    primarySignificatorPlanets: ['Mercury', 'Venus'],
    description: 'Boost commercial success, clients, negotiations, trade, and partnerships.'
  },
  leadership: {
    id: 'leadership',
    name: 'Leadership & Command',
    primaryHouses: [1, 5, 10],
    supportingHouses: [9, 11],
    primarySignificatorPlanets: ['Sun', 'Mars'],
    description: 'Command respect, inspire followers, obtain governance power, and political stature.'
  },
  authority: {
    id: 'authority',
    name: 'Authority & Status',
    primaryHouses: [1, 5, 9, 10],
    supportingHouses: [],
    primarySignificatorPlanets: ['Sun', 'Jupiter'],
    description: 'Attain higher social rank, executive decision-making capabilities, and administrative power.'
  },
  fame: {
    id: 'fame',
    name: 'Fame & Public Image',
    primaryHouses: [1, 5, 10, 11],
    supportingHouses: [7],
    primarySignificatorPlanets: ['Moon', 'Venus', 'Sun'],
    description: 'Gain public appreciation, positive celebrity status, and widespread recognition.'
  },
  wealth: {
    id: 'wealth',
    name: 'Wealth & Finance',
    primaryHouses: [2, 5, 9, 11],
    supportingHouses: [1],
    primarySignificatorPlanets: ['Jupiter', 'Venus', 'Mercury'],
    description: 'Attract financial stability, liquid assets, multiple revenue sources, and smart investments.'
  },
  education: {
    id: 'education',
    name: 'Education & Studies',
    primaryHouses: [4, 5, 9],
    supportingHouses: [1, 2],
    primarySignificatorPlanets: ['Mercury', 'Jupiter'],
    description: 'Excel in academics, competitive exams, memory recall, and specialized credentials.'
  },
  marriage: {
    id: 'marriage',
    name: 'Marriage & Relationship',
    primaryHouses: [7, 2, 11],
    supportingHouses: [1, 5], // Careful with 8th (marital bond) but avoiding negative aspects
    primarySignificatorPlanets: ['Venus', 'Jupiter'],
    description: 'Harmonize marital bonds, attract a compatible partner, and settle relationship disputes.'
  },
  children: {
    id: 'children',
    name: 'Children & Progeny',
    primaryHouses: [5, 9, 2],
    supportingHouses: [11],
    primarySignificatorPlanets: ['Jupiter'],
    description: 'Support safe childbirth, child health, emotional connection with kids, and progeny happiness.'
  },
  property: {
    id: 'property',
    name: 'Property & Real Estate',
    primaryHouses: [4, 2, 11],
    supportingHouses: [9],
    primarySignificatorPlanets: ['Mars', 'Saturn'],
    description: 'Settle home purchases, land acquisition, agricultural trades, or legacy inheritance.'
  },
  health: {
    id: 'health',
    name: 'Health & Vitality',
    primaryHouses: [1, 5], // Note: 6,8,12 are health-related but we NEVER strengthen their lords unless they are functional benefics. We strengthen 1st & 9th houses to fight diseases.
    supportingHouses: [9],
    primarySignificatorPlanets: ['Sun', 'Mars'],
    description: 'Boost immune system, mental resilience, energy levels, and recover from chronic illness.'
  },
  competition: {
    id: 'competition',
    name: 'Competition & Sports',
    primaryHouses: [3, 6, 10, 11],
    supportingHouses: [1],
    primarySignificatorPlanets: ['Mars', 'Saturn'],
    description: 'Conquer competitors, defeat legal obstacles, succeed in active sports or competitive debates.'
  },
  spiritual: {
    id: 'spiritual',
    name: 'Spirituality & Liberation',
    primaryHouses: [5, 9, 12],
    supportingHouses: [4, 8],
    primarySignificatorPlanets: ['Jupiter', 'Ketu', 'Sun'],
    description: 'Advance in meditation, mantra siddhi, finding spiritual guides, and self-realization.'
  },
  occult: {
    id: 'occult',
    name: 'Occult & Astrology',
    primaryHouses: [5, 8, 9, 12],
    supportingHouses: [1],
    primarySignificatorPlanets: ['Ketu', 'Jupiter', 'Mercury'],
    description: 'Enhance intuitive abilities, astrology calculations, tarot reading, or metaphysical studies.'
  },
  creativity: {
    id: 'creativity',
    name: 'Creativity & Arts',
    primaryHouses: [3, 5, 10, 11],
    supportingHouses: [],
    primarySignificatorPlanets: ['Venus', 'Moon'],
    description: 'Enhance artistic talents, acting, musical skills, designing, or creative writing.'
  }
};

export const GEMSTONE_DETAILS: { [planet in PlanetName]?: { name: string; substitute: string; metal: string; finger: string; day: string; mantra: string; color: string; weight: string } } = {
  Sun: {
    name: 'Ruby (Manikya)',
    substitute: 'Red Spinel / Rubelite',
    metal: 'Gold / Copper',
    finger: 'Ring Finger',
    day: 'Sunday morning',
    mantra: 'Om Hroom Hreem Hroum Sah Suryaya Namah',
    color: 'Crimson Red',
    weight: '3.5 - 5.5 Ratti (Min 1/12th body weight)'
  },
  Moon: {
    name: 'Natural Pearl (Moti)',
    substitute: 'Moonstone / White Coral',
    metal: 'Silver',
    finger: 'Little Finger',
    day: 'Monday morning',
    mantra: 'Om Shram Shreem Shroum Sah Chandraya Namah',
    color: 'Pearly White',
    weight: '4.25 - 6.5 Ratti (Natural Basra/South Sea)'
  },
  Mars: {
    name: 'Red Coral (Moonga)',
    substitute: 'Carnelian / Red Jasper',
    metal: 'Copper / Gold',
    finger: 'Ring Finger',
    day: 'Tuesday morning',
    mantra: 'Om Kram Kreem Kroum Sah Bhaumaya Namah',
    color: 'Coral Orange/Red',
    weight: '6.25 - 9.25 Ratti (Italian/Triangular)'
  },
  Mercury: {
    name: 'Emerald (Panna)',
    substitute: 'Green Tourmaline / Peridot',
    metal: 'Gold / Silver',
    finger: 'Little Finger',
    day: 'Wednesday morning',
    mantra: 'Om Bram Breem Broum Sah Budhaya Namah',
    color: 'Emerald Green',
    weight: '3.25 - 5.5 Ratti (Zambian/Colombian)'
  },
  Jupiter: {
    name: 'Yellow Sapphire (Pukhraj)',
    substitute: 'Yellow Topaz / Heliodor',
    metal: 'Gold',
    finger: 'Index Finger',
    day: 'Thursday morning',
    mantra: 'Om Gram Greem Groum Sah Gurave Namah',
    color: 'Golden Yellow',
    weight: '3.5 - 5.5 Ratti (Ceylon/Unheated)'
  },
  Venus: {
    name: 'Diamond (Heera)',
    substitute: 'White Sapphire / Clear Quartz',
    metal: 'Platinum / White Gold / Silver',
    finger: 'Middle Finger / Ring Finger',
    day: 'Friday morning',
    mantra: 'Om Dram Dreem Droum Sah Shukraya Namah',
    color: 'Translucent White / Diamond Sparkle',
    weight: '0.50 - 1.50 Carats / 4.25+ Ratti for White Sapphire'
  },
  Saturn: {
    name: 'Blue Sapphire (Neelam)',
    substitute: 'Amethyst / Blue Spinel / Iolite',
    metal: 'Panchdhatu / Iron / Silver',
    finger: 'Middle Finger',
    day: 'Saturday evening',
    mantra: 'Om Pram Preem Proum Sah Shanishcharaya Namah',
    color: 'Deep Royal Blue',
    weight: '3.5 - 6.5 Ratti (Ceylon/Kashmir)'
  },
  Rahu: {
    name: 'Hessonite (Gomed)',
    substitute: 'Orange Zircon',
    metal: 'Silver / Alloy',
    finger: 'Middle Finger',
    day: 'Saturday night (Rahu hora)',
    mantra: 'Om Bhram Bhreem Bhroum Sah Rahave Namah',
    color: 'Honey Brown',
    weight: '4.5 - 7.5 Ratti (Ceylon Honey-color)'
  },
  Ketu: {
    name: 'Cat\'s Eye (Lehsuniya)',
    substitute: 'Tiger\'s Eye',
    metal: 'Silver',
    finger: 'Middle Finger / Ring Finger',
    day: 'Tuesday morning / night',
    mantra: 'Om Stram Streem Stroum Sah Ketave Namah',
    color: 'Chrysoberyl Greenish-Yellow with Chatoyancy',
    weight: '3.5 - 6.25 Ratti (Chrysoberyl with sharp ray)'
  }
};

const ZODIAC_LORDS_LIST = [
  'Mars',    // Aries (0)
  'Venus',   // Taurus (1)
  'Mercury', // Gemini (2)
  'Moon',    // Cancer (3)
  'Sun',     // Leo (4)
  'Mercury', // Virgo (5)
  'Venus',   // Libra (6)
  'Mars',    // Scorpio (7)
  'Jupiter', // Sagittarius (8)
  'Saturn',  // Capricorn (9)
  'Saturn',  // Aquarius (10)
  'Jupiter'  // Pisces (11)
];

const SIGN_INDEX_MAP: { [key in ZodiacSign]: number } = {
  Aries: 0, Taurus: 1, Gemini: 2, Cancer: 3, Leo: 4, Virgo: 5,
  Libra: 6, Scorpio: 7, Sagittarius: 8, Capricorn: 9, Aquarius: 10, Pisces: 11
};

/**
 * SOURCE TRANSPARENCY REFERENCE LIST
 */
export const JYOTISHA_SOURCES = {
  functionalLordship: { rule: 'Lagna-lord is always benefic. Trikona lords (5th, 9th) are highly auspicious. Kendra lords (4th, 7th, 10th) lose natural maleficence/beneficence. Dusthana lords (6th, 8th, 12th) bring trials.', source: 'Brihat Parashara Hora Shastra (BPHS), Chapter 34', confidence: 'Absolute' },
  stoneStrengthening: { rule: 'Gemstones do not change functional nature; they amplify the planet’s power. Strengthening a functional malefic brings suffering.', source: 'Garuda Purana (Purva Khanda, Ratna Pariksha) & Parashari Anukul Tradition [Attribution: Unverified in Phaladeepika Ch. 24]', confidence: 'Traditional Consensus (Unverified in Phaladeepika)' },
  d9Navamsha: { rule: 'A planet weak in D1 but vargottama or strong in D9 has hidden resilience.', source: 'Saravali, Chapter 28 / Jataka Parijata', confidence: 'High' },
  yogakaraka: { rule: 'A single planet owning both a Kendra and Trikona becomes Yogakaraka, granting immense success.', source: 'Brihat Parashara Hora Shastra, Chapter 34, Verse 11', confidence: 'Absolute' }
};

/**
 * 1. FUNCTIONAL LORDSHIP ENGINE
 * Dynamically determines lordship house numbers for every planet based on the Ascendant (D1 house 1 sign).
 */
export function getFunctionalLordships(ascendantSign: ZodiacSign): { [planet in PlanetName]?: number[] } {
  const ascIndex = SIGN_INDEX_MAP[ascendantSign];
  const lordships: { [planet in PlanetName]?: number[] } = {};

  // Loop through 12 houses
  for (let houseNum = 1; houseNum <= 12; houseNum++) {
    const signIndex = (ascIndex + (houseNum - 1)) % 12;
    const lord = ZODIAC_LORDS_LIST[signIndex] as PlanetName;
    if (!lordships[lord]) {
      lordships[lord] = [];
    }
    lordships[lord]?.push(houseNum);
  }
  return lordships;
}

/**
 * Calculates a functional beneficence score out of 100
 */
export function calculateFunctionalBeneficence(planet: PlanetName, lordships: number[], isLagnaLord: boolean, placedHouse?: number): { score: number; classification: string } {
  // Special Handling for Shadow Planets (Rahu & Ketu)
  if (planet === 'Rahu') {
    if (placedHouse && [3, 6, 10, 11].includes(placedHouse)) {
      return { score: 80, classification: 'Functional Benefic (Shadow Planet in Upachaya House)' };
    }
    if (placedHouse && [1, 4, 5, 7, 9].includes(placedHouse)) {
      return { score: 72, classification: 'Auspicious Placement (Shadow Planet in Kendra/Trikona)' };
    }
    if (placedHouse && [8, 12].includes(placedHouse)) {
      return { score: 35, classification: 'Functional Malefic (Shadow Planet in Dusthana)' };
    }
    return { score: 65, classification: 'Neutral Shadow Planet (Chhaya Graha)' };
  }

  if (planet === 'Ketu') {
    if (placedHouse && [5, 9, 12].includes(placedHouse)) {
      return { score: 85, classification: 'Mokshakaraka Benefic (Spiritual Trikona / 12th House)' };
    }
    if (placedHouse && [3, 11].includes(placedHouse)) {
      return { score: 75, classification: 'Upachaya Benefic (Ketu in 3rd/11th House)' };
    }
    if (placedHouse && [8].includes(placedHouse)) {
      return { score: 38, classification: 'Volatile Placement (Ketu in 8th House)' };
    }
    return { score: 65, classification: 'Neutral Spiritual Catalyst (Ketu)' };
  }

  // Lagna lord is always auspicious
  if (isLagnaLord) {
    return { score: 95, classification: 'Highest Benefic (Lagna Lord)' };
  }

  let isKendra = false;
  let isTrikona = false;
  let isDusthana = false;
  let isTrishadaya = false;

  lordships.forEach(h => {
    if ([4, 7, 10].includes(h)) isKendra = true;
    if ([5, 9].includes(h)) isTrikona = true;
    if ([6, 8, 12].includes(h)) isDusthana = true;
    if ([3, 6, 11].includes(h)) isTrishadaya = true;
  });

  // Yogakaraka
  if (isKendra && isTrikona) {
    return { score: 100, classification: 'Yogakaraka (Ultimate Benefic)' };
  }

  // Pure Trikona Lord
  if (isTrikona && !isDusthana) {
    return { score: 88, classification: 'Functional Benefic (Trikona Lord)' };
  }

  // Kendra Lord (with neutral or no dusthana)
  if (isKendra && !isDusthana) {
    return { score: 75, classification: 'Functional Benefic (Kendra Lord)' };
  }

  // Mixed Lordship (Trikona + Dusthana, e.g. Mars for Scorpio Lagna ruling 1st & 6th, or Jupiter ruling 9th & 12th for Aries)
  if (isTrikona && isDusthana) {
    return { score: 70, classification: 'Mainly Benefic (Mixed Trikona & Dusthana)' };
  }

  if (isKendra && isDusthana) {
    return { score: 62, classification: 'Mixed (Kendra & Dusthana)' };
  }

  // Dusthana lords
  if (isDusthana) {
    const isLordOfEight = lordships.includes(8);
    const isLordOfSix = lordships.includes(6);
    if (isLordOfEight) {
      // Sun or Moon ruling 8th is not as malefic as per classical Jyotisha
      if (planet === 'Sun' || planet === 'Moon') {
        return { score: 45, classification: 'Neutral (8th Lord Sun/Moon)' };
      }
      return { score: 20, classification: 'Functional Malefic (8th Lord)' };
    }
    if (isLordOfSix) {
      return { score: 25, classification: 'Functional Malefic (6th Lord)' };
    }
    return { score: 35, classification: 'Functional Malefic/Neutral (12th Lord)' };
  }

  // Trishadaya (3, 11)
  if (isTrishadaya) {
    return { score: 40, classification: 'Functional Malefic (Trishadaya Lord of Wealth/Struggle)' };
  }

  // Default neutral/mildly benefic (e.g. 2nd lord alone)
  return { score: 60, classification: 'Neutral Benefic' };
}

/**
 * ADVANCED PERSONALIZED JYOTISHA GEMSTONE ADVISOR ENGINE
 */
export function analyzeGemstoneSuitability(
  kundali: CompleteKundali,
  objectiveId: string,
  userSelectedPlanet?: PlanetName
): GemstoneSuitabilityReport[] {
  if (!kundali || !kundali.houses || kundali.houses.length === 0 || !kundali.planets) {
    return [];
  }
  const ascendantSign = kundali.houses[0]?.sign || 'Aries';
  const lordships = getFunctionalLordships(ascendantSign);
  const currentDashaPlanet = kundali.dashas?.find(d => d.isCurrent)?.planet || 'Sun';

  // Get objective guidelines
  const objective = OBJECTIVES[objectiveId] || OBJECTIVES.general;

  // 1. Identify governing planets and significators for this specific objective
  const primaryRulers: PlanetName[] = [];
  const supportingRulers: PlanetName[] = [];
  const primaryOccupants: PlanetName[] = [];
  const primarySignificators = objective.primarySignificatorPlanets;

  for (const [pName, houses] of Object.entries(lordships)) {
    const pl = pName as PlanetName;
    if (houses?.some(h => objective.primaryHouses.includes(h))) {
      primaryRulers.push(pl);
    }
    if (houses?.some(h => objective.supportingHouses.includes(h))) {
      supportingRulers.push(pl);
    }
  }

  kundali.planets.forEach(p => {
    if (objective.primaryHouses.includes(p.house)) {
      if (!primaryOccupants.includes(p.name as PlanetName)) {
        primaryOccupants.push(p.name as PlanetName);
      }
    }
  });

  const lagnaLord = (lordships ? Object.entries(lordships).find(([_, h]) => h?.includes(1))?.[0] : undefined) as PlanetName | undefined;

  // Include all 9 Navagrahas for a complete, transparent assessment
  const allClassicalPlanets: PlanetName[] = ['Sun', 'Moon', 'Mars', 'Mercury', 'Jupiter', 'Venus', 'Saturn', 'Rahu', 'Ketu'];
  let planetsToAnalyze: PlanetName[] = [...allClassicalPlanets];

  if (userSelectedPlanet && allClassicalPlanets.includes(userSelectedPlanet)) {
    // Put user-chosen planet at the head of the evaluation queue
    planetsToAnalyze = [
      userSelectedPlanet,
      ...allClassicalPlanets.filter(p => p !== userSelectedPlanet)
    ];
  }

  const reports: GemstoneSuitabilityReport[] = [];

  for (const planet of planetsToAnalyze) {
    const details = GEMSTONE_DETAILS[planet];
    if (!details) continue;

    const pD1 = kundali.planets.find(p => p.name === planet);
    if (!pD1) continue;

    const pLordships = lordships[planet] || [];
    const isLagnaLord = pLordships.includes(1);

    // Dynamic functional lordship score
    const funcStatus = calculateFunctionalBeneficence(planet, pLordships, isLagnaLord, pD1.house);
    const isYogakaraka = funcStatus.classification.includes('Yogakaraka');

    // Determine Objective Role
    const isRulerOfPrimaryObjHouse = pLordships.some(h => objective.primaryHouses.includes(h));
    const isRulerOfSupportingObjHouse = pLordships.some(h => objective.supportingHouses.includes(h));
    const isKaraka = primarySignificators.includes(planet);
    const rulesDusthana = pLordships.some(h => [6, 8, 12].includes(h));

    let objectiveRole: 'PRIMARY_CONTENDER' | 'SUPPORTING' | 'NEUTRAL' | 'CONTRAINDICATED' = 'NEUTRAL';
    if (isRulerOfPrimaryObjHouse || isKaraka || isLagnaLord || isYogakaraka) {
      objectiveRole = 'PRIMARY_CONTENDER';
    } else if (isRulerOfSupportingObjHouse || objective.primaryHouses.includes(pD1.house)) {
      objectiveRole = 'SUPPORTING';
    } else if (rulesDusthana && !isLagnaLord && !isYogakaraka) {
      objectiveRole = 'CONTRAINDICATED';
    }

    // -------------------------------------------------------------
    // FACTOR 1: D1 POSITION & DIGNITY (Sign status, Exaltation/Debilitation, Placements)
    // -------------------------------------------------------------
    let d1Score = 50;
    let d1Status: 'positive' | 'neutral' | 'negative' | 'caution' = 'neutral';
    let d1Desc = '';

    if (pD1.dignity === 'Exalted') {
      d1Score = 95;
      d1Status = 'positive';
      d1Desc = `Exalted in ${pD1.sign}. Exceptionally high innate strength.`;
    } else if (pD1.dignity === 'Moolatrikona') {
      d1Score = 90;
      d1Status = 'positive';
      d1Desc = `Moolatrikona in ${pD1.sign}. Highly auspicious and stable.`;
    } else if (pD1.dignity === 'Own House') {
      d1Score = 85;
      d1Status = 'positive';
      d1Desc = `Placed in own sign ${pD1.sign}. Strongly anchored.`;
    } else if (['Great Friend', 'Friend'].includes(pD1.dignity)) {
      d1Score = 70;
      d1Status = 'positive';
      d1Desc = `Placed in friendly sign ${pD1.sign}. Harmonious placement.`;
    } else if (['Enemy', 'Great Enemy'].includes(pD1.dignity)) {
      d1Score = 30;
      d1Status = 'negative';
      d1Desc = `Placed in hostile sign ${pD1.sign}. Strained expression.`;
    } else if (pD1.dignity === 'Debilitated') {
      d1Score = 15;
      d1Status = 'negative';
      d1Desc = `Debilitated in ${pD1.sign}. Innate strength is heavily compromised.`;
    } else {
      d1Desc = `Placed in neutral sign ${pD1.sign}.`;
    }

    // Adjust for house placement in D1
    if ([6, 8, 12].includes(pD1.house)) {
      d1Score -= 20;
      d1Desc += ` Placed in Dusthana (House ${pD1.house}). Strong potential for adversity if strengthened.`;
      if (d1Status === 'positive') d1Status = 'caution';
    } else if ([1, 5, 9].includes(pD1.house)) {
      d1Score += 10;
      d1Desc += ` Fortified in Trikona (House ${pD1.house}).`;
    } else if ([4, 7, 10].includes(pD1.house)) {
      d1Score += 8;
      d1Desc += ` Placed in Kendra (House ${pD1.house}).`;
    }

    // -------------------------------------------------------------
    // FACTOR 2: SHADBALA (Innate planetary strength)
    // -------------------------------------------------------------
    let shadbalaScore = 50;
    let shadbalaStatus: 'positive' | 'neutral' | 'negative' | 'caution' = 'neutral';
    let shadbalaDesc = 'Standard strength.';
    let strengthCategory = 'Moderate';
    let shadbalaRatio = 1.0;

    const pShad = kundali.shadbala?.planets.find(s => s.planet === planet);
    if (pShad) {
      strengthCategory = pShad.strengthCategory;
      shadbalaRatio = pShad.ratio;
      shadbalaScore = Math.min(Math.round(shadbalaRatio * 70), 100);

      if (strengthCategory === 'Very Strong') {
        shadbalaStatus = 'caution'; // Caution of over-strengthening
        shadbalaDesc = `Extremely strong (Shadbala ratio: ${shadbalaRatio.toFixed(2)}). Already fully operational.`;
      } else if (strengthCategory === 'Strong') {
        shadbalaStatus = 'positive';
        shadbalaDesc = `Well supported (Shadbala ratio: ${shadbalaRatio.toFixed(2)}). Highly capable.`;
      } else if (strengthCategory === 'Moderate') {
        shadbalaStatus = 'neutral';
        shadbalaDesc = `Moderate capacity (Shadbala ratio: ${shadbalaRatio.toFixed(2)}). May benefit from assistance.`;
      } else if (strengthCategory === 'Weak') {
        shadbalaStatus = 'negative';
        shadbalaDesc = `Weak (Shadbala ratio: ${shadbalaRatio.toFixed(2)}). Struggles to manifest outcomes.`;
      }
    } else {
      if (pD1.dignity === 'Exalted' || pD1.dignity === 'Moolatrikona') {
        shadbalaScore = 85;
        strengthCategory = 'Strong';
        shadbalaDesc = 'Calculated strong through highly favorable zodiac dignity.';
        shadbalaStatus = 'positive';
      } else if (pD1.dignity === 'Debilitated') {
        shadbalaScore = 25;
        strengthCategory = 'Weak';
        shadbalaDesc = 'Calculated weak due to debilitated zodiac placement.';
        shadbalaStatus = 'negative';
      }
    }

    // -------------------------------------------------------------
    // FACTOR 3: D9 CONFIRMATION
    // -------------------------------------------------------------
    let d9Score = 50;
    let d9Status: 'positive' | 'neutral' | 'negative' | 'caution' = 'neutral';
    let d9Desc = 'No significant D9 deviation.';
    let isVargottama = false;

    const pD9 = kundali.charts.D9?.planets.find(p => p.name === planet);
    if (pD9) {
      isVargottama = pD1.sign === pD9.sign;
      if (isVargottama) {
        d9Score = 95;
        d9Status = 'positive';
        d9Desc = `Vargottama (same sign in D1 & D9). Highly stable, pure celestial influence.`;
      } else if (pD9.dignity === 'Exalted') {
        d9Score = 90;
        d9Status = 'positive';
        d9Desc = `Exalted in Navamsha (${pD9.sign}). Signifies strong hidden potential and inner resilience.`;
      } else if (pD9.dignity === 'Own House' || pD9.dignity === 'Moolatrikona') {
        d9Score = 80;
        d9Status = 'positive';
        d9Desc = `Strongly placed in own Navamsha sign (${pD9.sign}).`;
      } else if (pD9.dignity === 'Debilitated') {
        d9Score = 15;
        d9Status = 'negative';
        d9Desc = `Debilitated in Navamsha (${pD9.sign}). Initial D1 strength is heavily diluted on deeper levels (Bhanga risk).`;
      } else if (['Enemy', 'Great Enemy'].includes(pD9.dignity)) {
        d9Score = 35;
        d9Status = 'neutral';
        d9Desc = `Placed in uncomfortable sign in Navamsha (${pD9.sign}).`;
      } else {
        d9Score = 60;
        d9Desc = `Placed in neutral/friendly sign in Navamsha (${pD9.sign}).`;
      }
    }

    // -------------------------------------------------------------
    // FACTOR 4: RELEVANT DIVISIONAL CHART (Varga confirmation)
    // -------------------------------------------------------------
    let vargaScore = 50;
    let vargaStatus: 'positive' | 'neutral' | 'negative' | 'caution' = 'neutral';
    let vargaName = 'D9';
    let vargaDesc = 'Varga support evaluated from Navamsha.';

    let targetVargaCode = 'D9';
    if (['career', 'leadership', 'authority'].includes(objectiveId)) {
      targetVargaCode = 'D10';
      vargaName = 'Dasamsha (D10)';
    } else if (['wealth', 'business'].includes(objectiveId)) {
      targetVargaCode = 'D2';
      vargaName = 'Hora (D2)';
    } else if (objectiveId === 'education') {
      targetVargaCode = 'D24';
      vargaName = 'Siddhamsha (D24)';
    } else if (objectiveId === 'marriage') {
      targetVargaCode = 'D9';
      vargaName = 'Navamsha (D9)';
    } else if (objectiveId === 'children') {
      targetVargaCode = 'D7';
      vargaName = 'Saptamsha (D7)';
    } else if (objectiveId === 'property') {
      targetVargaCode = 'D4';
      vargaName = 'Chaturthamsha (D4)';
    } else if (objectiveId === 'spiritual') {
      targetVargaCode = 'D20';
      vargaName = 'Vimshamsha (D20)';
    }

    const targetVargaChart = (kundali.charts as any)?.[targetVargaCode];
    if (targetVargaChart) {
      const pVarga = targetVargaChart.planets?.find((p: any) => p.name === planet);
      if (pVarga) {
        if (pVarga.dignity === 'Exalted') {
          vargaScore = 90;
          vargaStatus = 'positive';
          vargaDesc = `Exalted in ${vargaName} (${pVarga.sign}). Confirms superb focus for ${objective.name}.`;
        } else if (pVarga.dignity === 'Own House' || pVarga.dignity === 'Moolatrikona') {
          vargaScore = 80;
          vargaStatus = 'positive';
          vargaDesc = `Strongly anchored in own sign in ${vargaName} (${pVarga.sign}).`;
        } else if (pVarga.dignity === 'Debilitated') {
          vargaScore = 20;
          vargaStatus = 'negative';
          vargaDesc = `Debilitated in ${vargaName} (${pVarga.sign}). Weak divisional support, risks failure in efforts.`;
        } else if (['Great Friend', 'Friend'].includes(pVarga.dignity)) {
          vargaScore = 70;
          vargaStatus = 'positive';
          vargaDesc = `Placed in friendly sign in ${vargaName} (${pVarga.sign}).`;
        } else {
          vargaScore = 50;
          vargaDesc = `Placed in neutral/mixed sign in ${vargaName} (${pVarga.sign}).`;
        }
      }
    } else {
      vargaDesc = `No specific ${targetVargaCode} divisional data present. Using D9 base support instead.`;
      vargaScore = d9Score;
      vargaStatus = d9Status;
    }

    // -------------------------------------------------------------
    // FACTOR 5: BHAVA (House occupancy & lordship relative to objective)
    // -------------------------------------------------------------
    let bhavaScore = 50;
    let bhavaStatus: 'positive' | 'neutral' | 'negative' | 'caution' = 'neutral';
    let bhavaDesc = '';

    const ruledHouses = pLordships;
    const placedHouse = pD1.house;

    const isPlacedInPrimaryObjHouse = objective.primaryHouses.includes(placedHouse);
    const isPlacedInSupportingObjHouse = objective.supportingHouses.includes(placedHouse);

    if (isRulerOfPrimaryObjHouse && isPlacedInPrimaryObjHouse) {
      bhavaScore = 95;
      bhavaStatus = 'positive';
      bhavaDesc = `Rules house(s) ${ruledHouses.join(', ')} and sits in House ${placedHouse} (both core to ${objective.name}). Exceptional alignment.`;
    } else if (isRulerOfPrimaryObjHouse) {
      bhavaScore = 85;
      bhavaStatus = 'positive';
      bhavaDesc = `Rules core house(s) ${ruledHouses.join(', ')} for ${objective.name}. Active controller.`;
    } else if (isPlacedInPrimaryObjHouse) {
      bhavaScore = 80;
      bhavaStatus = 'positive';
      bhavaDesc = `Occupies core House ${placedHouse} for ${objective.name}. Direct local impact.`;
    } else if (isRulerOfSupportingObjHouse) {
      bhavaScore = 65;
      bhavaStatus = 'positive';
      bhavaDesc = `Rules supporting house(s) ${ruledHouses.join(', ')} for ${objective.name}. Secondary supporter.`;
    } else if (isPlacedInSupportingObjHouse) {
      bhavaScore = 60;
      bhavaStatus = 'positive';
      bhavaDesc = `Placed in supporting House ${placedHouse} for ${objective.name}. Indirect supporter.`;
    } else {
      bhavaScore = 40;
      bhavaDesc = `Rules houses ${ruledHouses.join(', ')} and sits in House ${placedHouse}. Minimal structural connection to ${objective.name}.`;
    }

    if (rulesDusthana && !isLagnaLord && !isYogakaraka) {
      bhavaScore -= 15;
      bhavaStatus = 'caution';
      bhavaDesc += ` Note: Planet rules Dusthana House(s), raising risk of side effects.`;
    }

    // -------------------------------------------------------------
    // FACTOR 6: ASHTAKAVARGA SUPPORT
    // -------------------------------------------------------------
    let ashtakavargaScore = 50;
    let ashtakavargaStatus: 'positive' | 'neutral' | 'negative' | 'caution' = 'neutral';
    let ashtakavargaDesc = 'Average energy distribution.';

    const savScore = kundali.ashtakvarga ? getSavByBhava(kundali.ashtakvarga, placedHouse, kundali.houses) : undefined;
    const bavScore = kundali.ashtakvarga ? getBavByBhava(kundali.ashtakvarga, planet, placedHouse, kundali.houses) : undefined;

    if (savScore !== undefined || bavScore !== undefined) {
      const detailsText = [];
      if (bavScore !== undefined) {
        detailsText.push(`BAV score: ${bavScore}/8`);
        if (bavScore >= 5) {
          ashtakavargaScore += 15;
          ashtakavargaStatus = 'positive';
        } else if (bavScore <= 2) {
          ashtakavargaScore -= 15;
          ashtakavargaStatus = 'negative';
        }
      }
      if (savScore !== undefined) {
        detailsText.push(`SAV score: ${savScore}/56`);
        if (savScore >= 28) {
          ashtakavargaScore += 10;
          if (ashtakavargaStatus !== 'negative') ashtakavargaStatus = 'positive';
        } else if (savScore < 25) {
          ashtakavargaScore -= 10;
          if (ashtakavargaStatus !== 'positive') ashtakavargaStatus = 'negative';
        }
      }
      ashtakavargaScore = Math.max(10, Math.min(100, ashtakavargaScore));
      ashtakavargaDesc = `Energy points in House ${placedHouse} are ${ashtakavargaStatus === 'positive' ? 'robust' : ashtakavargaStatus === 'negative' ? 'deficient' : 'moderate'} (${detailsText.join(', ')}).`;
    }

    // -------------------------------------------------------------
    // FACTOR 7: YOGAS & CLASSICAL CONJUNCTIONS
    // -------------------------------------------------------------
    let yogaScore = 50;
    let yogaStatus: 'positive' | 'neutral' | 'negative' | 'caution' = 'neutral';
    let yogaDesc = 'No major classical yoga involvements.';
    const involvedYogas: string[] = [];

    if (isYogakaraka) {
      involvedYogas.push('Raja Yogakaraka status');
    }
    if (isLagnaLord) {
      involvedYogas.push('Lagnadhipati dynamic support');
    }
    
    const sameHousePlanets = kundali.planets.filter(p => p.house === placedHouse && p.name !== planet);
    if (sameHousePlanets.length > 0) {
      const coPlanets = sameHousePlanets.map(p => p.name);
      involvedYogas.push(`Conjunction with ${coPlanets.join(', ')}`);
      
      const hasMaleficConjunction = sameHousePlanets.some(p => ['Saturn', 'Mars', 'Rahu', 'Ketu'].includes(p.name));
      if (hasMaleficConjunction && !['Saturn', 'Mars'].includes(planet)) {
        yogaScore -= 10;
        yogaStatus = 'caution';
        yogaDesc = `Conjoined with malefic force(s). High danger of amplifying chaotic vibrations.`;
      }
    }

    if (involvedYogas.length > 0) {
      yogaScore = Math.min(95, yogaScore + involvedYogas.length * 10);
      if (yogaStatus !== 'caution') yogaStatus = 'positive';
      yogaDesc = `Participates in: ${involvedYogas.join(', ')}.`;
    }

    // -------------------------------------------------------------
    // FACTOR 8: NAKSHATRA & TARABALA
    // -------------------------------------------------------------
    let nakshatraScore = 50;
    let nakshatraStatus: 'positive' | 'neutral' | 'negative' | 'caution' = 'neutral';
    const pNakLord = pD1.nakshatraLord;
    const pNakName = pD1.nakshatra;

    // Calculate Navatara Tara Bala relative to natal Moon
    const moonPlanet = kundali.planets?.find(p => p.name === 'Moon');
    const moonNakIdx = moonPlanet?.nakshatraIndex ?? (kundali.panchang ? NAKSHATRAS.findIndex(n => n.name.toLowerCase() === kundali.panchang?.nakshatra?.toLowerCase()) : 0);
    const planetNakIdx = pD1.nakshatraIndex ?? NAKSHATRAS.findIndex(n => n.name.toLowerCase() === pNakName?.toLowerCase());
    const taraDiff = ((planetNakIdx >= 0 ? planetNakIdx : 0) - (moonNakIdx >= 0 ? moonNakIdx : 0) + 27) % 27;
    const taraIdx = taraDiff % 9;
    const TARA_NAMES = ['Janma', 'Sampat', 'Vipat', 'Kshema', 'Pratyak', 'Sadhana', 'Naidhana', 'Mitra', 'Param Mitra'];
    const taraName = TARA_NAMES[taraIdx] || 'Janma';
    const isNaidhana = taraName === 'Naidhana';
    const isHarmoniousTara = ['Sampat', 'Kshema', 'Sadhana', 'Mitra', 'Param Mitra'].includes(taraName);
    const isDangerousTara = ['Vipat', 'Pratyak', 'Naidhana'].includes(taraName);

    let nakshatraDesc = `Sits in ${pNakName} (ruled by ${pNakLord}) with ${taraName} Tara from natal Moon.`;
    
    if (isNaidhana) {
      nakshatraScore = 15;
      nakshatraStatus = 'caution';
    } else if (isDangerousTara) {
      nakshatraScore = 30;
      nakshatraStatus = 'negative';
    } else if (isHarmoniousTara) {
      nakshatraScore = 75;
      nakshatraStatus = 'positive';
    }

    const nakLordD1 = kundali.planets.find(p => p.name === pNakLord);
    if (nakLordD1) {
      if (['Exalted', 'Own House', 'Moolatrikona'].includes(nakLordD1.dignity)) {
        nakshatraScore = Math.min(100, nakshatraScore + 15);
        if (nakshatraStatus === 'neutral') nakshatraStatus = 'positive';
        nakshatraDesc += ` Nakshatra Lord ${pNakLord} is highly dignified (${nakLordD1.dignity}), amplifying positive transit resonance.`;
      } else if (nakLordD1.dignity === 'Debilitated' || [6, 8, 12].includes(nakLordD1.house)) {
        nakshatraScore = Math.max(10, nakshatraScore - 15);
        if (nakshatraStatus !== 'caution') nakshatraStatus = 'negative';
        nakshatraDesc += ` Nakshatra Lord ${pNakLord} is weak or in Dusthana (House ${nakLordD1.house}), causing unstable baseline energies.`;
      }
    }

    // -------------------------------------------------------------
    // FACTOR 9: DASHA TIMING SUPPORT
    // -------------------------------------------------------------
    let dashaScore = 50;
    let dashaStatus: 'positive' | 'neutral' | 'negative' | 'caution' = 'neutral';
    let dashaDesc = '';

    const isCurrentDasha = planet === currentDashaPlanet;
    const dashaObj = kundali.dashas.find(d => d.planet === planet);
    
    if (isCurrentDasha) {
      dashaScore = 95;
      dashaStatus = 'positive';
      dashaDesc = `Current Mahadasha Lord. Highly active period. Gemstone will have immediate and strong effects.`;
    } else {
      dashaScore = 60;
      dashaStatus = 'neutral';
      dashaDesc = `Dasha not currently active. Upcoming active dasha starts around ${dashaObj?.startDate || 'future dates'}.`;
    }

    // -------------------------------------------------------------
    // FACTOR 10: TRANSIT TIMING SUPPORT
    // -------------------------------------------------------------
    let transitScore = 50;
    let transitStatus: 'positive' | 'neutral' | 'negative' | 'caution' = 'neutral';
    let transitDesc = 'Transit influence is balanced.';

    if (planet === 'Saturn') {
      const isSadeSati = kundali.sadeSati?.isInSadeSati;
      if (isSadeSati) {
        transitScore = 75;
        transitStatus = 'caution';
        transitDesc = `Currently undergoing Sade Sati (${kundali.sadeSati?.phase}). Blue Sapphire can mitigate hurdles IF Saturn is functional benefic, otherwise it must be avoided.`;
      }
    } else if (planet === 'Jupiter') {
      transitScore = 80;
      transitStatus = 'positive';
      transitDesc = `Jupiter transiting favorable areas, bringing supportive expansive energies.`;
    }

    // -------------------------------------------------------------
    // FACTOR 11: RISKS / AFFLICTIONS
    // -------------------------------------------------------------
    let riskScore = 90;
    let riskStatus: 'positive' | 'neutral' | 'negative' | 'caution' = 'positive';
    let riskDesc = 'Pristine state. No afflictions found.';
    const activeRisks: string[] = [];

    if (pD1.isCombust) {
      riskScore -= 30;
      activeRisks.push('Combustion (Aditya-Chopa)');
    }
    if (pD1.isRetrograde) {
      riskScore -= 15;
      activeRisks.push('Retrogression (Vakra - highly unpredictable behavior)');
    }
    if ([6, 8, 12].includes(placedHouse)) {
      riskScore -= 35;
      activeRisks.push(`Dusthana placement in House ${placedHouse}`);
    }
    
    const rules6or8 = ruledHouses.some(h => [6, 8].includes(h));
    if (rules6or8 && !isLagnaLord) {
      riskScore -= 20;
      activeRisks.push(`Rules negative houses (${ruledHouses.filter(h => [6,8].includes(h)).join(', ')})`);
    }

    const isOverlyStrong = pD1.dignity === 'Exalted' && strengthCategory === 'Very Strong';
    if (isOverlyStrong) {
      riskScore -= 10;
      activeRisks.push('Over-strengthening risk (Planet is already exceptionally strong)');
    }

    if (isNaidhana) {
      riskScore -= 35;
      activeRisks.push('Naidhana (Vadh) Tara placement');
    }

    if (activeRisks.length > 0) {
      riskStatus = riskScore > 65 ? 'caution' : 'negative';
      riskDesc = `Afflictions present: ${activeRisks.join(', ')}. Caution recommended.`;
    }

    // -------------------------------------------------------------
    // OVERALL SUITABILITY MATHEMATICAL CALCULATION
    // Multi-factor project heuristic weights:
    // (Functional Lordship 35%, D1 Dignity 20%, D9 Navamsha 15%, Bhava Placement 15%,
    // Shadbala 7%, Ashtakavarga 3%, Dasha Timing 5%).
    // Note: These weights represent a calibrated project synthesis model under Anukul-vada,
    // NOT ancient classical verse measurements. Mathematical scores NEVER override verified
    // classical contraindications (pure Dusthana lords & Naidhana Tara cap score <= 40).
    // -------------------------------------------------------------
    const baseScore = (funcStatus.score * 0.35) + (d1Score * 0.20) + (d9Score * 0.15) + (bhavaScore * 0.15) + (shadbalaScore * 0.07) + (ashtakavargaScore * 0.03) + (dashaScore * 0.05);
    const riskFactor = riskScore / 100;
    let finalScore = Math.round(baseScore * riskFactor);

    const rules8th = ruledHouses.includes(8);
    const rules6th = ruledHouses.includes(6);

    let suitability: SuitabilityLevel = 'NOT RECOMMENDED';
    if (finalScore >= 82) {
      suitability = 'STRONGLY SUITABLE';
    } else if (finalScore >= 68) {
      suitability = 'SUITABLE';
    } else if (finalScore >= 52) {
      suitability = 'CONDITIONALLY SUITABLE';
    } else if (finalScore >= 35) {
      suitability = 'CAUTION';
    } else {
      suitability = 'NOT RECOMMENDED';
    }

    // Critical Overrides (Classical Parashari Anukul Ratna Shastra):
    const isSevereFunctionalMalefic = (rules8th || rules6th) && !isLagnaLord && !isYogakaraka;
    const isPureDusthanaTrishadayaMalefic = isSevereFunctionalMalefic && !ruledHouses.some(h => [1, 5, 9].includes(h));

    if (isPureDusthanaTrishadayaMalefic) {
      // Pure Dusthana / Trishadaya lords (e.g. 6th/8th without any Trikona) must NEVER be recommended
      if (['STRONGLY SUITABLE', 'SUITABLE', 'CONDITIONALLY SUITABLE'].includes(suitability)) {
        suitability = 'CAUTION';
      }
      if (finalScore > 40) {
        finalScore = Math.min(finalScore, 40);
      }
      objectiveRole = 'CONTRAINDICATED';
    } else if (isSevereFunctionalMalefic) {
      // Mixed Dusthana + Trikona/Kendra (e.g. 8th & 9th lord Saturn for Gemini, or 8th lord Sun/Moon)
      if (suitability === 'STRONGLY SUITABLE' || suitability === 'SUITABLE') {
        suitability = 'CONDITIONALLY SUITABLE';
      }
      if (finalScore > 58) {
        finalScore = 55;
      }
    }

    if (pD1.dignity === 'Debilitated' && (suitability === 'STRONGLY SUITABLE' || suitability === 'SUITABLE')) {
      suitability = 'CONDITIONALLY SUITABLE';
      if (finalScore > 58) {
        finalScore = 55;
      }
    }

    if (isNaidhana) {
      // Planets in Naidhana (Vadh) Tara are strictly contraindicated from gemstone wear
      if (['STRONGLY SUITABLE', 'SUITABLE', 'CONDITIONALLY SUITABLE'].includes(suitability)) {
        suitability = 'CAUTION';
      }
      if (finalScore > 40) {
        finalScore = Math.min(finalScore, 40);
      }
      objectiveRole = 'CONTRAINDICATED';
    }

    let confidence: 'High' | 'Moderate' | 'Low' = 'Moderate';
    if (pShad && targetVargaChart) {
      confidence = 'High';
    } else if (!pShad && !targetVargaChart) {
      confidence = 'Low';
    }

    // -------------------------------------------------------------
    // GENERATING ROBUST EXPLANATORY EVIDENCE (Primary Reasons & Cautions)
    // -------------------------------------------------------------
    const primaryReasons: string[] = [];
    const cautionPoints: string[] = [];

    // 1. Lordship / Astrological Identity Reason
    if (isLagnaLord) {
      primaryReasons.push(`As Lagna Lord, ${planet} is the primary guardian of your overall vitality, intellect, and worldly success.`);
    } else if (isYogakaraka) {
      primaryReasons.push(`Raja Yogakaraka status: Owns both a Kendra and Trikona house, granting ultimate planetary alignment for rapid elevation.`);
    } else if (isRulerOfPrimaryObjHouse) {
      primaryReasons.push(`Governs primary House ${ruledHouses.filter(h => objective.primaryHouses.includes(h)).join(' & ')}, directly controlling ${objective.name} outcomes.`);
    } else if (isKaraka) {
      primaryReasons.push(`Natural cosmic significator (Naisargika Karaka) for ${objective.name}, resonating with required vibrational frequencies.`);
    } else {
      primaryReasons.push(`Functional ${funcStatus.classification} with authority over House ${ruledHouses.join(' & ')}.`);
    }

    // 2. D1 Sign & House Dignity Reason
    if (['Exalted', 'Moolatrikona', 'Own House'].includes(pD1.dignity)) {
      primaryReasons.push(`Impeccable sign dignity: Placed in ${pD1.dignity} sign (${pD1.sign}) in House ${pD1.house}, supplying clean, potent celestial energy.`);
    } else if (['Great Friend', 'Friend'].includes(pD1.dignity)) {
      primaryReasons.push(`Harmonious zodiac placement in friendly sign (${pD1.sign}) within House ${pD1.house}, fostering smooth activation.`);
    } else if ([1, 4, 5, 7, 9, 10].includes(pD1.house)) {
      primaryReasons.push(`Fortified in ${[1, 5, 9].includes(pD1.house) ? 'auspicious Trikona' : 'pivotal Kendra'} (House ${pD1.house}), providing solid structural leverage.`);
    } else {
      primaryReasons.push(`Positioned in House ${pD1.house} with stable functional grounding in ${pD1.sign}.`);
    }

    // 3. Deep Structural Strengths (Shadbala, D9, Ashtakavarga)
    if (isVargottama) {
      primaryReasons.push(`Vargottama state across D1 & D9 Navamsha ensures resolute, unwavering realization of positive karmic fruits.`);
    } else if (pD9 && ['Exalted', 'Own House', 'Moolatrikona'].includes(pD9.dignity)) {
      primaryReasons.push(`Navamsha (D9) confirmation: Strong inner resilience in ${pD9.sign} (${pD9.dignity}) confirms lasting foundational strength.`);
    } else if (bavScore !== undefined && bavScore >= 4) {
      primaryReasons.push(`Favorable Ashtakavarga support with ${bavScore}/8 Bhinnashtakavarga bindus in House ${placedHouse}.`);
    } else {
      primaryReasons.push(`Balanced Shadbala operational capacity (${strengthCategory} virupa status: ${shadbalaRatio.toFixed(2)}x standard).`);
    }

    // 4. Timing Alignment
    if (isCurrentDasha) {
      primaryReasons.push(`Active Mahadasha lord: Wearing ${details.name} will trigger immediate and tangible positive shifts.`);
    } else {
      primaryReasons.push(`Vedic vibrational resonance: Supports ongoing transits and harmonious sub-periods.`);
    }

    if (isHarmoniousTara) {
      primaryReasons.push(`Harmonious Tarabala: Blessed by ${taraName} Tara from natal Moon.`);
    }

    // Build Caution Points (ensuring it's never empty)
    if (pD1.isCombust) {
      cautionPoints.push(`Planetary combustion (Aditya-Chopa): Close solar alignment requires gentle, gradual wearing protocol.`);
    }
    if (pD1.isRetrograde) {
      cautionPoints.push(`Retrogression (Vakra): Energy manifests in non-linear, introspective bursts; monitor subtle psychological reactions.`);
    }
    if (rulesDusthana) {
      cautionPoints.push(`Controls Dusthana house(s) (${ruledHouses.filter(h => [6,8,12].includes(h)).join(', ')}); ensure ring metal and weight comply strictly with Shastric norms.`);
    }
    if ([6, 8, 12].includes(placedHouse)) {
      cautionPoints.push(`Sits inside Dusthana House ${placedHouse}; a 5 to 7 day trial touch test is advised before permanent setting.`);
    }
    if (isOverlyStrong) {
      cautionPoints.push(`Exceptionally strong in Shadbala; avoid oversized carats to prevent aggressive or overwhelming energy surges.`);
    }

    if (isNaidhana) {
      cautionPoints.unshift(`Critical Tara Bala: Sits in Naidhana (Vadh) Tara from Janma Nakshatra; physical gemstone stimulation is contraindicated under Anukul Ratna Shastra.`);
    }

    if (isSevereFunctionalMalefic) {
      cautionPoints.unshift(`Classical Anukul Ratna Shastra: ${planet} functions as a malefic lord (${ruledHouses.map(h => formatOrdinal(h)).join(' & ')} house). Gemstones amplify incoming planetary radiation; wear is contraindicated or strictly cautionary. Perform Japa, Daana, Vrata, or Rudraksha Upayas instead.`);
    }

    // If no active afflictions, provide reassuring Vedic safety parameters
    if (cautionPoints.length === 0) {
      cautionPoints.push(`Pristine planetary state: Free from solar combustion, planetary war (Graha Yuddha), and negative Dusthana lordships.`);
      cautionPoints.push(`Safe energetic profile: Does not trigger Maraka or Badhaka hazards when energized using standard Vedic mantras.`);
      cautionPoints.push(`Balanced vibrational flow: Well-tolerated for long-term daily wearing across major life milestones.`);
    }

    // Build overall plain-English structured description
    let whyExplanation = '';
    if (isPureDusthanaTrishadayaMalefic) {
      whyExplanation = `Contraindicated under Classical Parashari Anukul Ratna Shastra. ${planet} rules severe Dusthana/Trishadaya house(s) (${ruledHouses.map(h => formatOrdinal(h)).join(' & ')}), functioning as a severe functional malefic. Gemstones amplify incoming planetary radiation and wearing ${details.name} risks magnifying disputes, health imbalances, or unforeseen obstacles. Shastric Upayas (Japa, Daana, Vrata, Rudraksha) are advised instead of physical gemstone wear.`;
    } else if (suitability === 'STRONGLY SUITABLE') {
      whyExplanation = `Excellent cosmic support. ${planet} is functionally magnificent for your chart as a ${funcStatus.classification}. Since it controls key houses and has superb structural dignity in D1 and ${vargaName}, wearing ${details.name} will heavily amplify positive outcomes for ${objective.name}.`;
    } else if (suitability === 'SUITABLE') {
      whyExplanation = `Favorable alignment. ${planet} acts as a solid supporter. Although it lacks the ultimate power of a Yogakaraka, its high D9 confirmation and strong functional lordship make ${details.name} a great tool to wear.`;
    } else if (suitability === 'CONDITIONALLY SUITABLE') {
      whyExplanation = `Mixed indicators detected. While ${planet} holds key lordship over ${objective.name} houses, certain afflictions like ${activeRisks.join(', ') || 'minor imbalances'} indicate that the gemstone should only be worn under specific periods or with purification rituals.`;
    } else if (suitability === 'CAUTION') {
      whyExplanation = `High risk of double-edged sword effects. ${planet} is conjoined with negative parameters or rules stressful sectors of life. Strengthening this planet might resolve one objective but could severely hamper other sectors like health or relationships. Approach with caution.`;
    } else {
      whyExplanation = `Not recommended. ${planet} rules core negative Dusthana houses or suffers severe structural damage in both D1 and ${vargaName}. Strengthening this force is highly likely to trigger obstacles, disputes, or physical stress.`;
    }

    // Timing guidance
    let timingGuidance = 'Wear on any auspicious sunrise on the designated day during Shukla Paksha (waxing moon phase).';
    if (isCurrentDasha) {
      timingGuidance = `Heavily active right now because you are running the ${planet} Mahadasha. Start wearing immediately on a ${details.day} morning after chanting the mantra 108 times.`;
    } else {
      timingGuidance = `Optimal results will be felt during the sub-periods (Antardasha) of ${planet} or when transiting Jupiter triggers your natal ${planet} position.`;
    }

    // Default or configured Vedic Upaya
    const upayaDetail = VEDIC_UPAYAS[planet] || {
      beejaMantra: `Om ${planet}aya Namah`,
      vedicMantra: `Vedic invocation for ${planet}`,
      japCount: '108 chants daily',
      mala: 'Rudraksha Rosary',
      deity: 'Presiding Divinity',
      daana: { items: ['Grains', 'Cloth', 'Dakshina'], day: details.day, time: 'Morning', recipient: 'Pious person or temple' },
      vrata: { day: details.day, deity: 'Presiding Deity', method: 'Fast with devotion' },
      rudraksha: { mukhi: 'Standard Mukhi', benefits: 'General harmony and balance' },
      lifestyle: ['Practice truthfulness, righteousness, and daily meditation.'],
      yantra: `${planet} Yantra`
    };

    reports.push({
      engineVersion: GEMSTONE_ENGINE_VERSION,
      planet,
      gemstone: details.name,
      functionalRole: pLordships.length > 0 ? `${pLordships.map(h => formatOrdinal(h)).join(' & ')} Lord (${funcStatus.classification})` : `No Lordship (${funcStatus.classification})`,
      suitability,
      confidence,
      overallScore: finalScore,
      objectiveRole,
      factors: {
        d1: { name: 'D1 Rashi', status: d1Status, value: pD1.dignity, description: d1Desc },
        shadbala: { name: 'Shadbala', status: shadbalaStatus, value: strengthCategory, description: shadbalaDesc },
        d9: { name: 'Navamsha (D9)', status: d9Status, value: pD9 ? pD9.dignity : 'Unknown', description: d9Desc },
        relevantVarga: { name: vargaName, status: vargaStatus, value: vargaStatus === 'positive' ? 'Strong' : vargaStatus === 'negative' ? 'Weak' : 'Moderate', description: vargaDesc },
        bhava: { name: 'Bhava Alignment', status: bhavaStatus, value: `House ${placedHouse}`, description: bhavaDesc },
        ashtakavarga: { name: 'Ashtakavarga', status: ashtakavargaStatus, value: bavScore !== undefined ? `${bavScore}/8` : 'Moderate', description: ashtakavargaDesc },
        yoga: { name: 'Yogas Involved', status: yogaStatus, value: involvedYogas.length > 0 ? 'Active' : 'None', description: yogaDesc },
        nakshatra: { name: 'Nakshatra', status: nakshatraStatus, value: pD1.nakshatra, description: nakshatraDesc },
        dasha: { name: 'Vimshottari Dasha', status: dashaStatus, value: isCurrentDasha ? 'Active Lord' : 'Inactive', description: dashaDesc },
        transit: { name: 'Transits', status: transitStatus, value: 'Balanced', description: transitDesc },
        risk: { name: 'Affliction & Risks', status: riskStatus === 'positive' ? 'positive' : 'caution', value: activeRisks.length > 0 ? 'Afflicted' : 'Clear', description: riskDesc }
      },
      whyExplanation,
      primaryReasons: primaryReasons.slice(0, 4),
      cautionPoints: cautionPoints.slice(0, 4),
      timingGuidance,
      upaya: upayaDetail
    });
  }

  // Sort reports by overall suitability score descending
  return reports.sort((a, b) => b.overallScore - a.overallScore);
}

export interface GemstoneDayTimeSlot {
  startTime: string;
  endTime: string;
  startIso: string;
  endIso: string;
  type: 'PRIMARY_HORA' | 'FRIEND_HORA' | 'ABHIJIT' | 'SHUBH_CHOGHADIYA' | 'PROHIBITED' | 'NEUTRAL';
  title: string;
  hora: string;
  choghadiya: string;
  isAuspicious: boolean;
  isAvoid: boolean;
  score: number;
  rationale: string;
}

export interface GemstoneTargetDateAnalysis {
  targetDateStr: string;
  formattedDate: string;
  planet: PlanetName;
  gemstone: string;
  vara: string;
  isPrescribedDay: boolean;
  prescribedDayName: string;
  tithi: string;
  isShuklaPaksha: boolean;
  nakshatra: string;
  taraBala: {
    taraName: string;
    isAuspicious: boolean;
    suitability: string;
    score: number;
  };
  overallDayScore: number;
  grade: 'EXCELLENT' | 'GOOD' | 'MODERATE' | 'CAUTION' | 'CONTRAINDICATED';
  gradeLabel: string;
  prohibitions: string[];
  auspiciousFactors: string[];
  timeSlots: GemstoneDayTimeSlot[];
  bestSlot: GemstoneDayTimeSlot | null;
  rahuKalam: { start: string; end: string };
  yamaganda: { start: string; end: string };
  abhijit: { start: string; end: string };
  nearestAuspiciousDate?: {
    dateStr: string;
    formattedDate: string;
    score: number;
    reason: string;
  };
}

const PLANET_FRIEND_HORAS: Partial<Record<PlanetName, PlanetName[]>> = {
  Sun: ['Moon', 'Mars', 'Jupiter'],
  Moon: ['Sun', 'Mercury'],
  Mars: ['Sun', 'Moon', 'Jupiter'],
  Mercury: ['Sun', 'Venus'],
  Jupiter: ['Sun', 'Moon', 'Mars'],
  Venus: ['Mercury', 'Saturn'],
  Saturn: ['Mercury', 'Venus'],
  Rahu: ['Mercury', 'Venus', 'Saturn'],
  Ketu: ['Sun', 'Mars', 'Jupiter']
};

/**
 * Calculates hourly Muhurta slots and Tarabala suitability for wearing a gemstone on a user-selected target date.
 */
export function calculateGemstoneDateMuhurta(
  targetDateStr: string,
  planet: PlanetName,
  kundali: CompleteKundali
): GemstoneTargetDateAnalysis {
  const parts = targetDateStr.split('-').map(Number);
  const year = parts[0] || 2026;
  const month = parts[1] ? parts[1] - 1 : 0;
  const day = parts[2] || 1;

  const dateObj = new Date(year, month, day, 12, 0, 0);
  const lat = kundali.birthDetails?.latitude || 28.6139;
  const lon = kundali.birthDetails?.longitude || 77.2090;

  const dailyFactors = getDailyPanchangaFactors(dateObj, lat, lon);
  const middayFactors = getTimePanchangaFactors(dateObj, dailyFactors);

  const jatakaNakName = kundali.panchang?.nakshatra || 'Ashwini';
  const jatakaNakIdx = Math.max(0, NAKSHATRAS.findIndex(n => n.name.toLowerCase() === jatakaNakName.toLowerCase()));
  const taraResult = calculateTaraBala(jatakaNakIdx, middayFactors.moonNakIdx);

  const gemDetail = GEMSTONE_DETAILS[planet] || { name: 'Gemstone', day: 'Sunday' };
  const prescribedDay = gemDetail.day;
  const isPrescribedDay = prescribedDay.toLowerCase().includes(dailyFactors.vara.toLowerCase());

  const isShuklaPaksha = middayFactors.tithi.toLowerCase().includes('shukla');
  const isRikta = [4, 9, 14, 19, 24, 29].includes(middayFactors.tithiNumber);
  const isAmavasya = middayFactors.tithiNumber === 30;

  const prohibitions: string[] = [];
  const auspiciousFactors: string[] = [];

  let dayScore = 50;

  if (isPrescribedDay) {
    dayScore += 25;
    auspiciousFactors.push(`Prescribed classical day (${dailyFactors.vara}) for ${planet} (${gemDetail.name})`);
  } else {
    prohibitions.push(`Non-ideal weekday (${dailyFactors.vara} vs recommended ${prescribedDay})`);
  }

  if (isShuklaPaksha) {
    dayScore += 15;
    auspiciousFactors.push(`Waxing Moon Phase (${middayFactors.tithi}) - favorable for energization`);
  } else {
    prohibitions.push(`Krishna Paksha (Waning phase) - reduces planetary radiance`);
  }

  if (['Sampat', 'Sadhaka', 'Kshema'].includes(taraResult.taraName)) {
    dayScore += 20;
    auspiciousFactors.push(`Strong Tarabala (${taraResult.taraName} Tara) - highly supportive of native's life vitality`);
  } else if (['Mitra', 'Param Mitra'].includes(taraResult.taraName)) {
    dayScore += 15;
    auspiciousFactors.push(`Harmonious Tarabala (${taraResult.taraName} Tara)`);
  } else if (['Vipat', 'Pratyari'].includes(taraResult.taraName)) {
    dayScore -= 20;
    prohibitions.push(`Challenging Tarabala (${taraResult.taraName} Tara) - potential obstacles`);
  } else if (taraResult.taraName === 'Naidhana') {
    dayScore -= 35;
    prohibitions.push(`Critical Tarabala (Naidhana / Vadh Tara) - strictly prohibited for consecration`);
  }

  if (isRikta) {
    dayScore -= 15;
    prohibitions.push(`Rikta Tithi (${middayFactors.tithi}) - hollow results`);
  }
  if (isAmavasya) {
    dayScore -= 30;
    prohibitions.push(`Amavasya (New Moon) - Moon devoid of digital rays`);
  }

  dayScore = Math.max(10, Math.min(100, Math.round(dayScore)));

  let grade: 'EXCELLENT' | 'GOOD' | 'MODERATE' | 'CAUTION' | 'CONTRAINDICATED' = 'MODERATE';
  let gradeLabel = 'Moderate / Neutral Day';

  if (dayScore >= 85) {
    grade = 'EXCELLENT';
    gradeLabel = 'Highly Auspicious (Utkrishta)';
  } else if (dayScore >= 70) {
    grade = 'GOOD';
    gradeLabel = 'Auspicious & Favorable (Shubha)';
  } else if (dayScore >= 50) {
    grade = 'MODERATE';
    gradeLabel = 'Acceptable / Neutral (Sadharana)';
  } else if (dayScore >= 35) {
    grade = 'CAUTION';
    gradeLabel = 'Requires Caution (Savadhana)';
  } else {
    grade = 'CONTRAINDICATED';
    gradeLabel = 'Inauspicious / Avoid (Ashubha)';
  }

  // Generate hourly segments from Sunrise to Sunset
  const sr = dailyFactors.sunrise;
  const ss = dailyFactors.sunset;
  const diurnalMs = ss.getTime() - sr.getTime();
  const horaMs = diurnalMs / 12;

  const timeSlots: GemstoneDayTimeSlot[] = [];
  const friendHoras = PLANET_FRIEND_HORAS[planet] || [];

  for (let h = 0; h < 12; h++) {
    const segStart = new Date(sr.getTime() + h * horaMs);
    const segEnd = new Date(segStart.getTime() + horaMs);
    const segMid = new Date(segStart.getTime() + horaMs / 2);

    const segPanchang = getTimePanchangaFactors(segMid, dailyFactors);
    const hora = segPanchang.hora;
    const chog = segPanchang.choghadiya?.name || 'Shubh';

    const isRahu = dailyFactors.rahuKalam.rawStart && dailyFactors.rahuKalam.rawEnd &&
      segMid.getTime() >= dailyFactors.rahuKalam.rawStart.getTime() &&
      segMid.getTime() <= dailyFactors.rahuKalam.rawEnd.getTime();

    const isYamaganda = dailyFactors.yamaganda.rawStart && dailyFactors.yamaganda.rawEnd &&
      segMid.getTime() >= dailyFactors.yamaganda.rawStart.getTime() &&
      segMid.getTime() <= dailyFactors.yamaganda.rawEnd.getTime();

    let type: GemstoneDayTimeSlot['type'] = 'NEUTRAL';
    let title = `${hora} Hora (${chog})`;
    let slotScore = Math.round(dayScore * 0.4);
    let isAvoid = false;
    let rationale = `Standard ${hora} Hora interval`;

    if (isRahu) {
      type = 'PROHIBITED';
      title = 'Rahu Kaalam (Strict Avoidance)';
      slotScore = 0;
      isAvoid = true;
      rationale = 'Inauspicious Rahu period; nullifies ritual consecration and introduces obstacles.';
    } else if (isYamaganda) {
      type = 'PROHIBITED';
      title = 'Yamaganda Window (Avoid)';
      slotScore = 15;
      isAvoid = true;
      rationale = 'Ketu/Yama influence; prefer standard horas.';
    } else {
      let isOwnHora = hora.toLowerCase() === planet.toLowerCase();
      let isFriendHora = friendHoras.some(f => f.toLowerCase() === hora.toLowerCase());

      if (isOwnHora) {
        type = 'PRIMARY_HORA';
        title = `★ ${planet} Own Hora + ${chog} Choghadiya`;
        slotScore += 45;
        rationale = `Optimal resonance: planetary lord's own hourly window energizes ${gemDetail.name} to full potency.`;
      } else if (isFriendHora) {
        type = 'FRIEND_HORA';
        title = `${hora} Hora (Planetary Friend) + ${chog}`;
        slotScore += 30;
        rationale = `Natural ally of ${planet}; supports gentle harmonious activation.`;
      }

      if (['Amrit', 'Shubh', 'Labh'].includes(chog)) {
        slotScore += 20;
        rationale += ` Blessed by ${chog} Choghadiya.`;
      } else if (['Rog', 'Kaal', 'Udveg'].includes(chog)) {
        slotScore -= 15;
        rationale += ` Inauspicious ${chog} Choghadiya reduces vitality.`;
      }
    }

    slotScore = Math.max(0, Math.min(100, slotScore));

    timeSlots.push({
      startTime: formatTime(segStart),
      endTime: formatTime(segEnd),
      startIso: segStart.toISOString(),
      endIso: segEnd.toISOString(),
      type,
      title,
      hora,
      choghadiya: chog,
      isAuspicious: slotScore >= 65 && !isAvoid,
      isAvoid,
      score: slotScore,
      rationale
    });
  }

  // Also include Abhijit Muhurta slot explicitly if active and not on Rahu
  if (dailyFactors.abhijit.rawStart && dailyFactors.abhijit.rawEnd) {
    const abhijitStart = dailyFactors.abhijit.rawStart;
    const abhijitEnd = dailyFactors.abhijit.rawEnd;
    const abhijitMid = new Date((abhijitStart.getTime() + abhijitEnd.getTime()) / 2);
    const abhijitPanchang = getTimePanchangaFactors(abhijitMid, dailyFactors);

    const isWednesday = dailyFactors.vara === 'Wednesday';
    let abhijitScore = isWednesday ? 40 : Math.round(dayScore * 0.4) + 40;
    if (['Amrit', 'Shubh', 'Labh'].includes(abhijitPanchang.choghadiya?.name || '')) {
      abhijitScore += 15;
    }
    abhijitScore = Math.max(10, Math.min(100, abhijitScore));

    timeSlots.push({
      startTime: formatTime(abhijitStart),
      endTime: formatTime(abhijitEnd),
      startIso: abhijitStart.toISOString(),
      endIso: abhijitEnd.toISOString(),
      type: 'ABHIJIT',
      title: isWednesday ? 'Abhijit Muhurta (Afflicted on Wednesday)' : '★ Abhijit Muhurta (Universal Benefic Window)',
      hora: abhijitPanchang.hora,
      choghadiya: abhijitPanchang.choghadiya?.name || 'Shubh',
      isAuspicious: !isWednesday && abhijitScore >= 65,
      isAvoid: isWednesday,
      score: abhijitScore,
      rationale: isWednesday
        ? 'Abhijit Muhurta is prohibited on Wednesdays as it forms Rahu-alignment dosha.'
        : 'Universal noon benefic window blessed by Lord Vishnu; overrides minor Panchanga blemishes.'
    });
  }

  // Sort slots by score descending
  timeSlots.sort((a, b) => b.score - a.score);

  const bestSlot = timeSlots.find(s => !s.isAvoid && s.score >= 50) || timeSlots[0] || null;

  // If dayScore is poor, find nearest auspicious date in next 14 days
  let nearestAuspiciousDate: GemstoneTargetDateAnalysis['nearestAuspiciousDate'] = undefined;
  if (dayScore < 65) {
    for (let offset = 1; offset <= 14; offset++) {
      const candDate = new Date(year, month, day + offset, 12, 0, 0);
      const candDaily = getDailyPanchangaFactors(candDate, lat, lon);
      const candMid = getTimePanchangaFactors(candDate, candDaily);
      const candTara = calculateTaraBala(jatakaNakIdx, candMid.moonNakIdx);
      const candPrescribed = gemDetail.day.toLowerCase().includes(candDaily.vara.toLowerCase());
      const candShukla = candMid.tithi.toLowerCase().includes('shukla');
      const candGoodTara = ['Sampat', 'Sadhaka', 'Kshema', 'Param Mitra'].includes(candTara.taraName);

      if (candPrescribed && (candShukla || candGoodTara)) {
        nearestAuspiciousDate = {
          dateStr: candDate.toISOString().split('T')[0],
          formattedDate: candDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }),
          score: 85,
          reason: `Falls on ${candDaily.vara} (${gemDetail.name}'s classical day) with ${candTara.taraName} Tara.`
        };
        break;
      }
    }
  }

  const formattedDate = dateObj.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });

  return {
    targetDateStr,
    formattedDate,
    planet,
    gemstone: gemDetail.name,
    vara: dailyFactors.vara,
    isPrescribedDay,
    prescribedDayName: gemDetail.day,
    tithi: middayFactors.tithi,
    isShuklaPaksha,
    nakshatra: middayFactors.nakshatra,
    taraBala: {
      taraName: taraResult.taraName,
      isAuspicious: ['Sampat', 'Kshema', 'Sadhaka', 'Mitra', 'Param Mitra'].includes(taraResult.taraName),
      suitability: taraResult.suitability,
      score: taraResult.suitability === 'Excellent' ? 25 : taraResult.suitability === 'Good' ? 18 : taraResult.suitability === 'Neutral' ? 10 : 0
    },
    overallDayScore: dayScore,
    grade,
    gradeLabel,
    prohibitions,
    auspiciousFactors,
    timeSlots,
    bestSlot,
    rahuKalam: {
      start: dailyFactors.rahuKalam.start,
      end: dailyFactors.rahuKalam.end
    },
    yamaganda: {
      start: dailyFactors.yamaganda.start,
      end: dailyFactors.yamaganda.end
    },
    abhijit: {
      start: dailyFactors.abhijit.start,
      end: dailyFactors.abhijit.end
    },
    nearestAuspiciousDate
  };
}
